import { useCallback, useEffect, useId, useState, type FormEvent, type ReactNode } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { useAdminTheme } from "../hooks/useAdminTheme";
import type { AdminTheme } from "../lib/adminTheme";
import { ThemeToggle } from "../components/ThemeToggle";
import { AdminLogsPanel } from "../components/AdminLogsPanel";
import { PanelCallPanel } from "../components/PanelCallPanel";
import { adminDownloadFile, adminPostJson, triggerBlobDownload } from "../lib/adminDownload";
import { FieldLabel } from "../components/FieldLabel";
import { HelpTooltip } from "../components/HelpTooltip";
import { TableCellText } from "../components/TableCellText";
import { ADMIN_SETTINGS_FIELD_HELP } from "../lib/adminSettingsFieldHelp";
import { AppShell, type NavItem } from "../components/AppShell";
import { Select, type SelectGroup } from "../components/ui/Select";
import { Checkbox, NumberField, Collapse } from "../components/ui/controls";
import { SegmentedControl, segPanelId, segTabId } from "../components/ui/SegmentedControl";
import { Modal } from "../components/ui/Modal";
import { LoadingState, EmptyState } from "../components/ui/states";
import { useToast } from "../components/ui/Toast";
import { useConfirm } from "../components/ui/ConfirmDialog";
import {
  IconSettings,
  IconSliders,
  IconUsers,
  IconFileText,
  IconPhone,
  IconPhoneCall,
  IconChat,
  IconTerminal,
  IconEdit,
  IconTrash,
  IconPlus,
  IconChevronDown,
  IconX,
  IconAlertCircle,
  IconEye,
  IconEyeOff,
} from "../components/ui/icons";

type TabId =
  | "crmContacts"
  | "settings"
  | "settingsTech"
  | "sipTrunks"
  | "contacts"
  | "logs"
  | "chat"
  | "transcripts"
  | "call";

type Settings = {
  masterPrompt: string | null;
  geminiScriptPromptPrefix: string | null;
  transferProposePrompt: string | null;
  transferPrompt: string | null;
  endCallPrompt: string | null;
  transferProposeAfterMinutes: number;
  callWarnText: string | null;
  callWarnAfterMinutes: number;
  callMaxDurationMinutes: number;
  crmSyncEnabled: boolean;
  crmSyncUrl: string | null;
  crmSyncTokenConfigured: boolean;
  /** Tylko do wysyłki: token wpisany w panelu. Serwer nigdy go nie zwraca. */
  crmSyncToken?: string;
  crmLastSyncAt: string | null;
  crmLastSyncStatus: string | null;
  crmLastSyncMessage: string | null;
  crmLastSyncCount: number;
  geminiLiveModelId: string;
  geminiThinkingBudget: number | null;
  geminiSilenceDurationMs: number;
  geminiChatModelId: string;
  geminiLiveVoiceName: string;
  voiceMode: "gemini_live";
  fillerEnabled: boolean;
  reconnectFillerEnabled: boolean;
  bargeInSensitivity: "conservative" | "balanced" | "aggressive";
  bargeInEnabled: boolean;
  stallWatchdogEnabled: boolean;
  hangupIntentFallbackEnabled: boolean;
  keepaliveEnabled: boolean;
  stallDetectMs: number;
  stallMaxReconnects: number;
  stallFreshSessionAfter: number;
  keepaliveIntervalMs: number;
  devToolsEnabled: boolean;
  devToolsExpiresAt: string | null;
  devToolsActive: boolean;
  transcriptRetentionDays: number;
  recordingRetentionDays: number;
  geminiApiKeyConfigured: boolean;
};

type ModelOpt = { id: string; label: string };
type VoiceOpt = { id: string; label: string; gender?: "female" | "male" };

type ChatMessage = { role: "user" | "model"; text: string };

type ChatTurnAudit = {
  turnIndex: number;
  at: string;
  userMessage: string;
  modelReply: string;
  llm: {
    model: string;
    requestText: string;
    responseText: string;
    inputTokens: number | null;
    outputTokens: number | null;
    durationMs: number;
  };
  systemInstruction: string;
};

const CHAT_AUDIT_STORAGE_KEY = "callbot-chat-audit-v1";

/** Dymek przy kolumnie „Wypowiedzi" — sama liczba nie zdradza, że powitanie liczy się zawsze. */
const CALL_ENTRIES_HELP =
  "Ile razy strony zabrały głos: każda wypowiedź dzwoniącego i asystentki to jedna pozycja. " +
  "Urywki mowy dzwoniącego wypowiedziane w odstępie do 1,5 s liczą się jako jedna wypowiedź. " +
  "Powitanie i pożegnanie zapisywane są z treści scenariusza, więc rozmowa z jedną wypowiedzią " +
  "nie dowodzi, że rozmówca cokolwiek usłyszał — to potwierdzi dopiero nagranie.";

type CallSession = {
  id: string;
  startedAt: string;
  endedAt: string | null;
  callerNumber: string | null;
  callerName: string | null;
  channelId: string | null;
  transferPending: boolean;
  entryCount: number;
  hasRecording: boolean;
  adminNote: string | null;
  hasAdminNote: boolean;
};

type Contact = {
  id: string;
  phoneNormalized: string;
  phoneRaw: string;
  firstName: string;
  lastName: string;
  note: string | null;
  promptAddendum: string | null;
  createdAt: string;
  updatedAt: string;
};

type CallSessionDetail = CallSession & {
  entries: {
    speaker: string;
    text: string;
    thinkingText?: string | null;
    secondsFromStart: number;
  }[];
};

// Zbalansowana liczba kolumn siatki pól: dzieli N elementów na wyrównane rzędy
// (6 -> 3+3, 8 -> 4+4, 5 -> 3+2), z sufitem 4 kolumn. Zwraca styl z --cols.
function balancedCols(n: number): number {
  if (n <= 1) return 1;
  const rows = Math.ceil(n / 4);
  return Math.ceil(n / rows);
}
// Styl inline opisany strukturalnie zamiast React.CSSProperties — typy Reacta nie są
// w tym projekcie zainstalowane (patrz komentarz przy no-undef w eslint.config.js).
type InlineStyle = Record<string, string | number>;
function gridCols(n: number, extra?: InlineStyle): InlineStyle {
  return { ...(extra ?? {}), ["--cols" as string]: balancedCols(n) };
}

function PageHead({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="page-head">
      <h1 className="page-title">{title}</h1>
      {sub ? <p className="page-sub">{sub}</p> : null}
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="banner" role="alert" style={{ marginBottom: 20 }}>
      <IconAlertCircle size={18} />
      <span>{message}</span>
    </div>
  );
}

function TranscriptEntryBlock({
  entry,
}: {
  entry: CallSessionDetail["entries"][number];
}) {
  return (
    <div className="transcript-entry">
      <div className="transcript-entry__meta">
        [{entry.secondsFromStart}s] {entry.speaker}:
      </div>
      <div className="transcript-entry__text">{entry.text}</div>
      {entry.speaker === "assistant" && entry.thinkingText?.trim() ? (
        <details className="transcript-thinking-details">
          <summary>Pokaż myślenie modelu</summary>
          <div className="transcript-thinking">{entry.thinkingText.trim()}</div>
        </details>
      ) : null}
    </div>
  );
}

type CallNoteModalState = {
  sessionId: string;
  callerNumber: string | null;
  startedAt: string;
  adminNote: string | null;
};

type SipRegisterTestResult = {
  ok: boolean;
  domain?: string;
  latencyMs?: number;
  testedAt?: string;
  error?: string;
};

const VOICE_PREVIEW_TEXT = "Dzień dobry, tu asystentka Your Mate AI. W czym mogę pomóc?";

async function fetchVoicePreviewAudio(path: string, body: Record<string, string>): Promise<Blob> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error ?? `HTTP ${res.status}`);
  }
  return res.blob();
}

function playAudioBlob(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const audio = new Audio(url);
  audio.onended = () => URL.revokeObjectURL(url);
  void audio.play().catch(() => {
    URL.revokeObjectURL(url);
  });
}

async function adminFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    credentials: "include",
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
  return data as T;
}

function AdminLogin({
  theme,
  onToggleTheme,
  onOk,
}: {
  theme: AdminTheme;
  onToggleTheme: () => void;
  onOk: () => void;
}) {
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sessionCheck, setSessionCheck] = useState<"pending" | "ready">("pending");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await adminFetch<{ authenticated: boolean }>("/admin/api/session");
        if (!cancelled && r.authenticated) nav("/admin/panel", { replace: true });
        else if (!cancelled) setSessionCheck("ready");
      } catch {
        if (!cancelled) setSessionCheck("ready");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nav]);

  const submit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    try {
      await adminFetch("/admin/api/login", {
        method: "POST",
        body: JSON.stringify({ password }),
      });
      onOk();
    } catch (ex) {
      setErr(String((ex as Error).message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ThemeToggle theme={theme} onToggle={onToggleTheme} fixed />
      <div className="admin-shell">
        <div className="auth-card">
          <div className="auth-head">
            <img className="auth-logo" src="/urmate-logo.webp" alt="urmate.ai" />
            <h1 className="auth-title">Panel callbota</h1>
            <p className="auth-subtitle">Zaloguj się, aby kontynuować</p>
          </div>
          {sessionCheck === "pending" ? (
            <LoadingState text="Ładowanie..." />
          ) : (
            <form onSubmit={(e) => void submit(e)} className="auth-form">
              <label className="admin-field-block auth-field">
                <div>Hasło</div>
                <div className="auth-input-wrap">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    placeholder="Wpisz hasło panelu"
                  />
                  <button
                    type="button"
                    className="auth-eye"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Ukryj hasło" : "Pokaż hasło"}
                    aria-pressed={showPassword}
                    tabIndex={-1}
                  >
                    {showPassword ? <IconEye size={20} /> : <IconEyeOff size={20} />}
                  </button>
                </div>
              </label>
              <div className={`auth-alert${err ? " is-visible" : ""}`} role="alert">
                <IconAlertCircle size={16} />
                <span>{err}</span>
              </div>
              <button type="submit" className="btn auth-submit" disabled={loading}>
                {loading ? (
                  <>
                    <span className="auth-spinner" aria-hidden="true" />
                    <span>Proszę czekać...</span>
                  </>
                ) : (
                  "Zaloguj"
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ Ustawienia */

function CheckboxSetting({
  checked,
  disabled,
  onChange,
  helpKey,
  label,
  hint,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
  helpKey: keyof typeof ADMIN_SETTINGS_FIELD_HELP;
  label: string;
  hint?: string;
}) {
  return (
    <div className="field-group" style={{ gap: 4 }}>
      <div className="row" style={{ gap: 8 }}>
        <Checkbox checked={checked} disabled={disabled} onChange={onChange}>
          {label}
        </Checkbox>
        <HelpTooltip text={ADMIN_SETTINGS_FIELD_HELP[helpKey]} label={`${label} - wyjaśnienie`} />
      </div>
      {hint ? <p className="muted" style={{ margin: 0 }}>{hint}</p> : null}
    </div>
  );
}

function SettingsTab({
  mode,
  settings,
  setSettings,
  liveModels,
  chatModels,
  geminiVoices,
  saving,
  onSave,
  onTestLlm,
  onTestSipRegister,
  sipTestLoading,
  sipTestResult,
  sipCooldownSec,
}: {
  mode: "basic" | "advanced";
  settings: Settings;
  setSettings: (s: Settings) => void;
  liveModels: ModelOpt[];
  chatModels: ModelOpt[];
  geminiVoices: VoiceOpt[];
  saving: boolean;
  onSave: () => Promise<void>;
  onTestLlm: () => void;
  onTestSipRegister: () => void;
  sipTestLoading: boolean;
  sipTestResult: SipRegisterTestResult | null;
  sipCooldownSec: number;
}) {
  const toast = useToast();
  const [previewText, setPreviewText] = useState(VOICE_PREVIEW_TEXT);
  const [geminiPreviewLoading, setGeminiPreviewLoading] = useState(false);
  const showBasic = mode === "basic";
  const showAdvanced = mode === "advanced";

  const voiceGroups: SelectGroup[] = (["female", "male"] as const)
    .map((g) => ({
      label: g === "female" ? "Głosy kobiece" : "Głosy męskie",
      options: geminiVoices.filter((v) => v.gender === g).map((v) => ({ value: v.id, label: v.label })),
    }))
    .filter((grp) => grp.options.length > 0);
  const otherVoices = geminiVoices.filter((v) => v.gender !== "female" && v.gender !== "male");
  if (otherVoices.length) {
    voiceGroups.push({ label: "Inne", options: otherVoices.map((v) => ({ value: v.id, label: v.label })) });
  }

  const previewGeminiVoice = async () => {
    if (!settings.geminiApiKeyConfigured) {
      toast.error("Brak GEMINI_API_KEY na serwerze.");
      return;
    }
    setGeminiPreviewLoading(true);
    try {
      const blob = await fetchVoicePreviewAudio("/admin/api/voices/gemini/preview", {
        modelId: settings.geminiLiveModelId,
        voiceName: settings.geminiLiveVoiceName,
        text: previewText.trim() || VOICE_PREVIEW_TEXT,
      });
      playAudioBlob(blob);
    } catch (e) {
      toast.error("Nie udało się odtworzyć głosu.", String((e as Error).message));
    } finally {
      setGeminiPreviewLoading(false);
    }
  };

  return (
    <div className="view-stack">
      {showBasic && (
        <div className="panel">
          <h3>Baza wiedzy</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            To, co asystentka WIE — jednakowe dla każdego numeru, obowiązuje od nowych połączeń
            i w chacie testowym. To, JAK ma się zachować na konkretnym numerze (powitanie, sekcja
            „kto dzwoni", dodatkowa instrukcja), ustawiasz w zakładce „Numery SIP" przy tym numerze.
          </p>
          <label className="admin-field-block" style={{ marginTop: 12 }}>
            <FieldLabel helpKey="masterPrompt">Master prompt (baza wiedzy)</FieldLabel>
            <textarea
              className="admin-textarea--master-prompt"
              rows={16}
              value={settings.masterPrompt ?? ""}
              onChange={(e) => setSettings({ ...settings, masterPrompt: e.target.value })}
            />
          </label>
        </div>
      )}

      {showBasic && (
        <div className="panel">
          <h3>Czas i komunikaty rozmowy</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            Prefiks Gemini, propozycja transferu, ostrzeżenie i limity czasu - od nowych połączeń głosowych.
          </p>
          <label className="admin-field-block" style={{ marginTop: 12 }}>
            <FieldLabel helpKey="geminiScriptPromptPrefix">Prefiks odtwarzania (Gemini Live)</FieldLabel>
            <p className="muted" style={{ margin: "4px 0 8px" }}>
              Przed skryptem początkowym, ostrzeżeniem i pożegnaniem; po prefiksie doklejany jest właściwy tekst.
            </p>
            <textarea
              rows={3}
              value={settings.geminiScriptPromptPrefix ?? ""}
              onChange={(e) => setSettings({ ...settings, geminiScriptPromptPrefix: e.target.value })}
            />
          </label>
          <label className="admin-field-block" style={{ marginTop: 12 }}>
            <FieldLabel helpKey="transferProposePrompt">Propozycja transferu do człowieka (instrukcja dla modelu)</FieldLabel>
            <textarea
              rows={4}
              value={settings.transferProposePrompt ?? ""}
              onChange={(e) => setSettings({ ...settings, transferProposePrompt: e.target.value })}
            />
          </label>
          <label className="admin-field-block" style={{ marginTop: 12, maxWidth: 320 }}>
            <FieldLabel helpKey="transferProposeAfterMinutes">Po ilu minutach zaproponować transfer</FieldLabel>
            <NumberField
              value={settings.transferProposeAfterMinutes}
              min={1}
              max={180}
              onChange={(v) => setSettings({ ...settings, transferProposeAfterMinutes: v ?? 0 })}
            />
          </label>
          <label className="admin-field-block" style={{ marginTop: 12 }}>
            <FieldLabel helpKey="callWarnText">Tekst ostrzeżenia (wypowiedziany przez asystentkę)</FieldLabel>
            <textarea
              rows={3}
              value={settings.callWarnText ?? ""}
              onChange={(e) => setSettings({ ...settings, callWarnText: e.target.value })}
            />
          </label>
          <div className="admin-settings-grid" style={gridCols(2, { marginTop: 12 })}>
            <label className="admin-field-block">
              <FieldLabel helpKey="callWarnAfterMinutes">Po ilu minutach ostrzeżenie</FieldLabel>
              <NumberField
                value={settings.callWarnAfterMinutes}
                min={1}
                max={180}
                onChange={(v) => setSettings({ ...settings, callWarnAfterMinutes: v ?? 0 })}
              />
            </label>
            <label className="admin-field-block">
              <FieldLabel helpKey="callMaxDurationMinutes">Maks. czas rozmowy (min)</FieldLabel>
              <NumberField
                value={settings.callMaxDurationMinutes}
                min={1}
                max={180}
                onChange={(v) => setSettings({ ...settings, callMaxDurationMinutes: v ?? 0 })}
              />
            </label>
          </div>
          <p className="muted" style={{ marginTop: 12, marginBottom: 0 }}>
            Wymagana kolejność: transfer &lt; ostrzeżenie &lt; max rozmowy.
          </p>
        </div>
      )}

      {showBasic && (
        <div className="panel">
          <h3>Narzędzia rozmowy</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            Transfer do człowieka i rozłączenie - obowiązują od nowych połączeń głosowych, jednakowo
            na każdym numerze. Chat testowy nie używa narzędzi. Każdy z tekstów idzie w dwa miejsca
            naraz: do promptu i jako opis narzędzia dla modelu. Puste pole = brak tekstu (bez
            ukrytego fallbacku); reguły z master promptu, jeśli się powtarzają, usuń stamtąd.
          </p>
          <label className="admin-field-block" style={{ marginTop: 12 }}>
            <FieldLabel helpKey="transferPrompt">Transfer do człowieka</FieldLabel>
            <textarea
              rows={6}
              value={settings.transferPrompt ?? ""}
              onChange={(e) => setSettings({ ...settings, transferPrompt: e.target.value })}
            />
          </label>
          <label className="admin-field-block" style={{ marginTop: 12 }}>
            <FieldLabel helpKey="endCallPrompt">Zakończenie rozmowy</FieldLabel>
            <textarea
              rows={5}
              value={settings.endCallPrompt ?? ""}
              onChange={(e) => setSettings({ ...settings, endCallPrompt: e.target.value })}
            />
          </label>
        </div>
      )}

      {showBasic && (
        <div className="panel">
          <h3>Głosy</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            Tryb mowy obowiązuje od <strong>nowych</strong> połączeń SIP. W jednej rozmowie cała mowa
            asystentki pochodzi z jednego źródła (głos Gemini Live).
          </p>
          <label className="admin-field-block" style={{ marginTop: 12 }}>
            <FieldLabel helpKey="voicePreviewText">Tekst podglądu</FieldLabel>
            <input
              type="text"
              value={previewText}
              onChange={(e) => setPreviewText(e.target.value)}
              maxLength={500}
            />
          </label>
          <label className="admin-field-block" style={{ marginTop: 12 }}>
            <FieldLabel helpKey="geminiLiveVoiceName">Głos Gemini Live</FieldLabel>
            <div className="row" style={{ gap: 8, alignItems: "stretch" }}>
              <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                <Select
                  ariaLabel="Głos Gemini Live"
                  value={settings.geminiLiveVoiceName}
                  groups={voiceGroups}
                  onChange={(v) => setSettings({ ...settings, geminiLiveVoiceName: v })}
                />
              </div>
              <button
                type="button"
                className="btn-secondary"
                disabled={!settings.geminiApiKeyConfigured || geminiPreviewLoading}
                title={settings.geminiApiKeyConfigured ? undefined : "Wymagany GEMINI_API_KEY na serwerze"}
                onClick={() => void previewGeminiVoice()}
              >
                {geminiPreviewLoading ? "Generuję..." : "Odtwórz głos Live"}
              </button>
            </div>
          </label>
        </div>
      )}

      {showAdvanced && (
        <div className="panel">
          <h3>Retencja i integracje</h3>
          <div className="admin-settings-grid" style={gridCols(6)}>
            <label className="admin-field-block">
              <FieldLabel helpKey="transcriptRetentionDays">Retencja transkryptów (dni)</FieldLabel>
              <NumberField value={settings.transcriptRetentionDays} min={0} onChange={(v) => setSettings({ ...settings, transcriptRetentionDays: v ?? 0 })} />
            </label>
            <label className="admin-field-block">
              <FieldLabel helpKey="recordingRetentionDays">Retencja nagrań (dni)</FieldLabel>
              <NumberField value={settings.recordingRetentionDays} min={0} onChange={(v) => setSettings({ ...settings, recordingRetentionDays: v ?? 0 })} />
            </label>
          </div>
          <div className="admin-field-block" style={{ marginTop: 20 }}>
            <h4 style={{ margin: "0 0 4px" }}>Klienci z CRM</h4>
            <p className="muted" style={{ margin: "0 0 10px" }}>
              Callbot pobiera katalog klientów z CRM co 15 minut i trzyma go u siebie, żeby
              rozpoznanie numeru w chwili odbierania połączenia było natychmiastowe. Token
              wygenerujesz w panelu CRM; zapisuje się zaszyfrowany i nigdy nie wraca do przeglądarki.
            </p>
            <div className="view-stack" style={{ gap: 12 }}>
              <label className="row" style={{ gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={settings.crmSyncEnabled}
                  onChange={(e) => setSettings({ ...settings, crmSyncEnabled: e.target.checked })}
                />
                <span>Pobieraj klientów z CRM</span>
              </label>
              <label className="admin-field-block">
                <div>Adres CRM</div>
                <input
                  type="text"
                  value={settings.crmSyncUrl ?? ""}
                  onChange={(e) => setSettings({ ...settings, crmSyncUrl: e.target.value })}
                  placeholder="https://crmrag.urmate.ai"
                />
              </label>
              <label className="admin-field-block">
                <div>Token dostępu {settings.crmSyncTokenConfigured ? "(ustawiony)" : "(brak)"}</div>
                <input
                  type="password"
                  value={settings.crmSyncToken ?? ""}
                  onChange={(e) => setSettings({ ...settings, crmSyncToken: e.target.value })}
                  placeholder={settings.crmSyncTokenConfigured ? "Wpisz, aby zmienić" : "urm_ai_..."}
                  autoComplete="new-password"
                />
                <p className="muted" style={{ marginTop: 6 }}>
                  Puste pole przy zapisie zostawia dotychczasowy token bez zmian.
                </p>
              </label>
              <p className="muted" style={{ margin: 0 }}>
                Ostatnia synchronizacja:{" "}
                {settings.crmLastSyncAt
                  ? `${new Date(settings.crmLastSyncAt).toLocaleString("pl-PL")} — ${
                      settings.crmLastSyncStatus === "ok" ? "udana" : "nieudana"
                    }${settings.crmLastSyncMessage ? ` (${settings.crmLastSyncMessage})` : ""}`
                  : "jeszcze nie było"}
              </p>
            </div>
          </div>

          <div className="view-stack" style={{ marginTop: 16, gap: 16 }}>
            <CheckboxSetting
              checked={settings.fillerEnabled}
              onChange={(v) => setSettings({ ...settings, fillerEnabled: v })}
              helpKey="fillerEnabled"
              label="Dźwięk oczekiwania (filler)"
            />
            <CheckboxSetting
              checked={settings.reconnectFillerEnabled}
              onChange={(v) => setSettings({ ...settings, reconnectFillerEnabled: v })}
              helpKey="reconnectFillerEnabled"
              label="Dźwięk fillera podczas rozłączania (reconnect)"
              hint="Sygnał WAV podczas przetwarzania po wypowiedzi użytkownika - nie jest głosem asystentki. Obowiązuje od nowych połączeń."
            />
          </div>
        </div>
      )}

      {showAdvanced && (
        <div className="panel">
          <h3>DevTools</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            API diagnostyczne <code>/dev-tools/*</code> dla lokalnego agenta (health-check, audyty rozmów, logi).
            Token jest zahardkodowany w repozytorium. Włączenie aktywuje DevTools na <strong>24 godziny</strong>,
            po czym wyłącza się automatycznie.
          </p>
          <div style={{ marginTop: 12 }}>
            <CheckboxSetting
              checked={settings.devToolsEnabled}
              onChange={(v) => setSettings({ ...settings, devToolsEnabled: v })}
              helpKey="devToolsEnabled"
              label="Włącz DevTools na 24 h"
              hint={
                settings.devToolsActive && settings.devToolsExpiresAt
                  ? `Aktywne do: ${new Date(settings.devToolsExpiresAt).toLocaleString("pl-PL")}. Zmiana obowiązuje po zapisaniu ustawień.`
                  : "Nieaktywne - całe /dev-tools/* zwraca 404. Zmiana obowiązuje po zapisaniu ustawień."
              }
            />
          </div>
        </div>
      )}

      {showAdvanced && (
        <div className="panel">
          <h3>Modele Google</h3>
          <label className="admin-field-block" style={{ marginTop: 8 }}>
            <FieldLabel helpKey="geminiLiveModelId">Live (rozmowa)</FieldLabel>
            <Select
              ariaLabel="Model Live"
              value={settings.geminiLiveModelId}
              options={liveModels.map((m) => ({ value: m.id, label: m.label }))}
              onChange={(v) => setSettings({ ...settings, geminiLiveModelId: v })}
            />
          </label>
          <label className="admin-field-block" style={{ marginTop: 12, maxWidth: 320 }}>
            <FieldLabel helpKey="geminiThinkingBudget">Thinking budget (Live)</FieldLabel>
            <NumberField
              value={settings.geminiThinkingBudget}
              min={0}
              max={24576}
              placeholder="domyślne API"
              onChange={(v) => setSettings({ ...settings, geminiThinkingBudget: v })}
            />
            <p className="muted" style={{ marginTop: 4, marginBottom: 0 }}>
              Puste = domyślne dynamic thinking. 0 = wyłączone. Wartość dodatnia = limit tokenów myślenia.
              Obowiązuje od nowych połączeń.
            </p>
          </label>
          <label className="admin-field-block" style={{ marginTop: 12, maxWidth: 320 }}>
            <FieldLabel helpKey="geminiSilenceDurationMs">Czas ciszy VAD (ms)</FieldLabel>
            <NumberField
              value={settings.geminiSilenceDurationMs}
              min={100}
              max={3000}
              onChange={(v) => setSettings({ ...settings, geminiSilenceDurationMs: v ?? 500 })}
            />
            <p className="muted" style={{ marginTop: 4, marginBottom: 0 }}>
              Po ilu milisekundach ciszy Gemini uznaje koniec wypowiedzi użytkownika. Niższa wartość =
              szybsza reakcja, wyższe ryzyko przerwania w trakcie mowy. Obowiązuje od nowych połączeń.
            </p>
          </label>
          <label className="admin-field-block" style={{ marginTop: 12 }}>
            <FieldLabel helpKey="bargeInSensitivity">Czułość przerywania (barge-in)</FieldLabel>
            <Select
              ariaLabel="Czułość przerywania"
              value={settings.bargeInSensitivity}
              options={[
                { value: "conservative", label: "Ostrożna - rzadziej fałszywe przerwania" },
                { value: "balanced", label: "Zbalansowana (zalecana)" },
                { value: "aggressive", label: "Agresywna - przerywa najłatwiej" },
              ]}
              onChange={(v) => setSettings({ ...settings, bargeInSensitivity: v as Settings["bargeInSensitivity"] })}
            />
            <p className="muted" style={{ marginTop: 4, marginBottom: 0 }}>
              Jak łatwo można przerwać asystentkę, gdy mówi. Agresywna = najbardziej naturalnie, ale większe
              ryzyko samoprzerwania od echa/szumu (głośnomówiący). Obowiązuje od nowych połączeń.
            </p>
          </label>
          <label className="admin-field-block" style={{ marginTop: 12 }}>
            <FieldLabel helpKey="geminiChatModelId">Chat testowy</FieldLabel>
            <Select
              ariaLabel="Model chatu testowego"
              value={settings.geminiChatModelId}
              options={chatModels.map((m) => ({ value: m.id, label: m.label }))}
              onChange={(v) => setSettings({ ...settings, geminiChatModelId: v })}
            />
          </label>
        </div>
      )}

      {showAdvanced && (
        <div className="panel">
          <h3>Niezawodność / utwardzenie (zaawansowane)</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            Domyślnie „goły callbot": agresywne mechanizmy naprawcze są <strong>wyłączone</strong> - czysta
            rozmowa bez samodzielnych reconnectów. Włączaj pojedynczo, jeśli chcesz twardszego odzyskiwania
            po zawieszeniach Gemini (kosztem ryzyka cięcia długich wypowiedzi).
          </p>
          <div className="view-stack" style={{ marginTop: 12, gap: 16 }}>
            <CheckboxSetting
              checked={settings.bargeInEnabled}
              onChange={(v) => setSettings({ ...settings, bargeInEnabled: v })}
              helpKey="bargeInEnabled"
              label="Przerywanie asystentki (barge-in)"
              hint="Gdy włączone, użytkownik może przerwać asystentkę mówiąc w trakcie jej wypowiedzi (czułość ustawiasz w sekcji Modele Google). Wyłączone = half-duplex: asystentka zawsze kończy zdanie. Obowiązuje od nowych połączeń."
            />
            <CheckboxSetting
              checked={settings.hangupIntentFallbackEnabled}
              onChange={(v) => setSettings({ ...settings, hangupIntentFallbackEnabled: v })}
              helpKey="hangupIntentFallbackEnabled"
              label="Awaryjne rozłączanie na prośbę rozmówcy (fallback)"
              hint="Gdy rozmówca wprost prosi o rozłączenie, a asystentka pożegna się, ale nie zakończy połączenia - system sam je zakończy po kilku sekundach ciszy. Zalecane włączone."
            />
            <div>
              <CheckboxSetting
                checked={settings.stallWatchdogEnabled}
                onChange={(v) => setSettings({ ...settings, stallWatchdogEnabled: v })}
                helpKey="stallWatchdogEnabled"
                label="Auto-reconnect przy zawieszeniu Gemini (stall-watchdog)"
                hint="Może ciąć długie wypowiedzi - gdy mówisz dłużej niż próg poniżej, Gemini słusznie milczy, a watchdog może pomylić to z awarią i zresetować rozmowę. Goły callbot = wyłączone. Działa od zaraz."
              />
              <Collapse open={settings.stallWatchdogEnabled}>
                <div style={{ marginLeft: 28, marginTop: 12 }} className="view-stack">
                  <label className="admin-field-block" style={{ maxWidth: 320 }}>
                    <FieldLabel helpKey="stallDetectMs">Próg wykrycia zawieszenia (ms)</FieldLabel>
                    <NumberField value={settings.stallDetectMs} min={2000} max={15000} onChange={(v) => setSettings({ ...settings, stallDetectMs: v ?? 6000 })} />
                  </label>
                  <label className="admin-field-block" style={{ maxWidth: 320 }}>
                    <FieldLabel helpKey="stallMaxReconnects">Maks. kolejnych reconnectów</FieldLabel>
                    <NumberField value={settings.stallMaxReconnects} min={2} max={20} onChange={(v) => setSettings({ ...settings, stallMaxReconnects: v ?? 6 })} />
                  </label>
                  <label className="admin-field-block" style={{ maxWidth: 320 }}>
                    <FieldLabel helpKey="stallFreshSessionAfter">Świeża sesja po N stallach</FieldLabel>
                    <NumberField value={settings.stallFreshSessionAfter} min={1} max={10} onChange={(v) => setSettings({ ...settings, stallFreshSessionAfter: v ?? 3 })} />
                  </label>
                  <p className="muted" style={{ margin: 0 }}>
                    Wyższy próg = mniej fałszywych cięć długich wypowiedzi. Świeża sesja po serii stalli
                    odzyskuje połączenie, ale gubi kontekst rozmowy (np. imię).
                  </p>
                </div>
              </Collapse>
            </div>
            <div>
              <CheckboxSetting
                checked={settings.keepaliveEnabled}
                onChange={(v) => setSettings({ ...settings, keepaliveEnabled: v })}
                helpKey="keepaliveEnabled"
                label="Keepalive WebSocket (ping)"
                hint="Lekki ping WS utrzymujący połączenie z Gemini (anty half-open-TCP). Łagodny - zalecane włączone. Obowiązuje od nowych połączeń."
              />
              <Collapse open={settings.keepaliveEnabled}>
                <label className="admin-field-block" style={{ marginTop: 12, marginLeft: 28, maxWidth: 320 }}>
                  <FieldLabel helpKey="keepaliveIntervalMs">Interwał keepalive (ms)</FieldLabel>
                  <NumberField value={settings.keepaliveIntervalMs} min={5000} max={60000} onChange={(v) => setSettings({ ...settings, keepaliveIntervalMs: v ?? 15000 })} />
                </label>
              </Collapse>
            </div>
          </div>
        </div>
      )}

      {showAdvanced && (
        <div className="panel">
          <h3>Trunk SIP z pliku .env (zaszłość)</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            Test odczytuje status jednego trunku wskazanego w pliku .env (pjsip show registration) -
            bez wysyłania REGISTER z Node. Numery dodane w panelu sprawdzisz w zakładce
            „Numery SIP", która pokazuje status każdego z nich osobno.
          </p>
          <div className="row" style={{ gap: 12, marginTop: 12 }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => void onTestSipRegister()}
              disabled={sipTestLoading || sipCooldownSec > 0}
            >
              {sipTestLoading
                ? "Testuję rejestrację..."
                : sipCooldownSec > 0
                  ? `Ponowny test za ${sipCooldownSec} s`
                  : "Testuj rejestrację SIP"}
            </button>
            {sipTestResult?.ok && (
              <span className="status-pill status-pill--ok">
                <span className="status-pill__dot" />
                OK - {sipTestResult.domain} ({sipTestResult.latencyMs} ms)
              </span>
            )}
            {sipTestResult && !sipTestResult.ok && (
              <span className="status-pill status-pill--warn">
                <span className="status-pill__dot" />
                {sipTestResult.error ?? "Test nieudany."}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="row" style={{ gap: 12 }}>
        <button type="button" className="btn" onClick={() => void onSave()} disabled={saving}>
          {saving ? "Zapisuję..." : "Zapisz zmiany"}
        </button>
        {showAdvanced && (
          <button type="button" className="btn-secondary" onClick={() => void onTestLlm()}>
            Uruchom test LLM
          </button>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Kontakty */

const EMPTY_CONTACT_FORM = {
  id: null as string | null,
  phone: "",
  firstName: "",
  lastName: "",
  note: "",
  promptAddendum: "",
};

type CrmContact = {
  id: string;
  phoneRaw: string;
  phoneNormalized: string;
  crmEntityType: string;
  displayName: string;
  crmStatus: string | null;
  statusDescription: string | null;
  aiStatus: string | null;
  aiContext: string | null;
  intervention: string | null;
  assignedUser: string | null;
  syncedAt: string;
  inContacts: boolean;
};

const CRM_PAGE_SIZE = 50;

/** Katalog z CRM: podgląd tylko do odczytu plus przeniesienie zaznaczonych do Kontaktów. */
function CrmContactsPanel() {
  const toast = useToast();
  const [items, setItems] = useState<CrmContact[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(CRM_PAGE_SIZE),
        offset: String(offset),
      });
      if (search.trim()) params.set("search", search.trim());
      const r = await adminFetch<{ items: CrmContact[]; total: number }>(
        `/admin/api/crm-contacts?${params.toString()}`,
      );
      setItems(r.items);
      setTotal(r.total);
      setLoadErr(null);
    } catch (e) {
      setLoadErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [search, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // „Zaznacz wszystkie" obejmuje tę stronę wyników — zaznaczanie tysięcy niewidocznych wierszy
  // byłoby zaskoczeniem przy klikaniu „Dodaj do kontaktów".
  const selectableIds = items.filter((i) => !i.inContacts).map((i) => i.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.includes(id));

  const toggleAll = () => {
    setSelected((prev) =>
      allSelected ? prev.filter((id) => !selectableIds.includes(id)) : [...new Set([...prev, ...selectableIds])],
    );
  };

  const copyToContacts = async () => {
    if (selected.length === 0) return;
    setBusy(true);
    try {
      const r = await adminFetch<{ result: { added: number; skipped: number } }>(
        "/admin/api/crm-contacts/to-contacts",
        { method: "POST", body: JSON.stringify({ ids: selected }) },
      );
      toast.success(
        "Przeniesiono do Kontaktów",
        `Dodano: ${r.result.added}, pominięto (już były): ${r.result.skipped}.`,
      );
      setSelected([]);
      await load();
    } catch (e) {
      toast.error("Nie udało się przenieść", (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const runSync = async () => {
    setBusy(true);
    try {
      const r = await adminFetch<{ result: { message: string } }>("/admin/api/crm-sync/run", {
        method: "POST",
      });
      toast.success("Synchronizacja zakończona", r.result.message);
      await load();
    } catch (e) {
      toast.error("Synchronizacja nieudana", (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="view-stack">
      <div className="panel">
        <h3>Klienci z CRM ({total})</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Ten katalog jest pobierany z CRM i odświeżany automatycznie — nie edytuje się go tutaj.
          Asystentka rozpoznaje po nim dzwoniących wraz z kontekstem sprawy. Żeby dopisać komuś
          własną instrukcję na rozmowę, przenieś go do Kontaktów: numery, które już tam są,
          zostają nietknięte.
        </p>
        <div className="row" style={{ gap: 12, marginTop: 12, flexWrap: "wrap" }}>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOffset(0);
            }}
            placeholder="Szukaj po nazwie, numerze lub statusie"
            style={{ minWidth: 260 }}
          />
          <button type="button" className="btn-secondary" onClick={() => void runSync()} disabled={busy}>
            Synchronizuj teraz
          </button>
          <button type="button" className="btn" onClick={() => void copyToContacts()} disabled={busy || selected.length === 0}>
            Dodaj do kontaktów ({selected.length})
          </button>
        </div>
      </div>

      <div className="panel">
        {loading ? (
          <LoadingState text="Ładowanie klientów z CRM..." />
        ) : loadErr ? (
          <EmptyState
            title="Nie udało się pobrać listy"
            desc={loadErr}
            icon={<IconAlertCircle size={22} />}
            action={
              <button type="button" className="btn-secondary" onClick={() => void load()}>
                Spróbuj ponownie
              </button>
            }
          />
        ) : items.length === 0 ? (
          <EmptyState
            title="Pusto"
            desc="Katalog jest pusty. Włącz synchronizację z CRM w Ustawieniach technicznych i wklej token."
            icon={<IconUsers size={22} />}
          />
        ) : (
          <>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        aria-label="Zaznacz wszystkie na tej stronie"
                      />
                    </th>
                    <th>Nazwa</th>
                    <th>Numer</th>
                    <th>Status</th>
                    <th>Na czym stanęło</th>
                    <th>Kontekst</th>
                    <th>W kontaktach</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.includes(c.id)}
                          onChange={() => toggle(c.id)}
                          disabled={c.inContacts}
                          aria-label={`Zaznacz ${c.displayName}`}
                        />
                      </td>
                      <td>{c.displayName}</td>
                      <td>{c.phoneRaw}</td>
                      <td style={{ maxWidth: 220 }}>
                        <TableCellText text={c.statusDescription ?? c.crmStatus} previewLength={80} />
                        {c.statusDescription && c.crmStatus && (
                          <div className="muted" style={{ fontSize: 11 }}>{c.crmStatus}</div>
                        )}
                      </td>
                      <td style={{ maxWidth: 300 }}>
                        <TableCellText text={c.aiStatus} />
                      </td>
                      <td style={{ maxWidth: 360 }}>
                        <TableCellText text={c.aiContext} />
                      </td>
                      <td>{c.inContacts ? "tak" : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="row" style={{ gap: 12, marginTop: 12, alignItems: "center" }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setOffset(Math.max(0, offset - CRM_PAGE_SIZE))}
                disabled={offset === 0}
              >
                Poprzednie
              </button>
              <span className="muted">
                {offset + 1}–{Math.min(offset + items.length, total)} z {total}
              </span>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setOffset(offset + CRM_PAGE_SIZE)}
                disabled={offset + items.length >= total}
              >
                Następne
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

type ImportEntry = { line: number; phone: string; name: string; action: string; fields: string[] };
type ImportProblem = { line: number; text: string; reason: string };
type ImportResult = {
  separator: string;
  headerSkipped: boolean;
  added: number;
  updated: number;
  problems: ImportProblem[];
  entries: ImportEntry[];
  dryRun: boolean;
};

/** Masowe wprowadzanie kontaktów: wklejenie z arkusza albo plik CSV, zawsze z podglądem. */
function ContactsImportPanel({ onImported }: { onImported: () => void | Promise<void> }) {
  const toast = useToast();
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportResult | null>(null);

  const send = async (dryRun: boolean) => {
    setBusy(true);
    setErr(null);
    try {
      let res: Response;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("dryRun", String(dryRun));
        res = await fetch("/admin/api/contacts/import", {
          method: "POST",
          credentials: "include",
          body: fd,
        });
      } else {
        res = await fetch("/admin/api/contacts/import", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text, dryRun }),
        });
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
      const result = data.result as ImportResult;
      setPreview(result);
      if (!dryRun) {
        toast.success(
          "Zaimportowano kontakty",
          `Dodano: ${result.added}, zaktualizowano: ${result.updated}.`,
        );
        setText("");
        setFile(null);
        await onImported();
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const nothingToSend = !text.trim() && !file;

  return (
    <div className="panel">
      <h3>Dodaj wiele numerów naraz</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        Kolejność kolumn: numer, imię, nazwisko, notatka, instrukcja. Rozdzielnik (tabulator,
        średnik lub przecinek) rozpoznaję sam, wiersz nagłówkowy pomijam. Kolumna, której nie
        podasz, zostaje nietknięta — import nie skasuje wpisanych wcześniej instrukcji.
      </p>
      <label className="admin-field-block" style={{ marginTop: 12 }}>
        <div>Wklej z arkusza</div>
        <textarea
          rows={6}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setPreview(null);
          }}
          placeholder={"509891745	Lesław	Kmiecik	klient	Zapytaj o wdrożenie"}
          disabled={busy || Boolean(file)}
        />
      </label>
      <label className="admin-field-block" style={{ marginTop: 12 }}>
        <div>…albo wskaż plik CSV</div>
        <input
          type="file"
          accept=".csv,text/csv,text/plain"
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            setPreview(null);
          }}
          disabled={busy}
        />
      </label>
      {err && (
        <p className="field-error" style={{ marginTop: 10 }}>
          <IconAlertCircle size={14} />
          {err}
        </p>
      )}
      <div className="row" style={{ gap: 12, marginTop: 14 }}>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => void send(true)}
          disabled={busy || nothingToSend}
        >
          {busy ? "Sprawdzam..." : "Sprawdź, co się stanie"}
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => void send(false)}
          disabled={busy || nothingToSend || !preview}
        >
          Zapisz kontakty
        </button>
      </div>
      {preview && (
        <div style={{ marginTop: 16 }}>
          <p className="muted" style={{ margin: 0 }}>
            {preview.dryRun ? "Podgląd" : "Wynik"}: dojdzie <strong>{preview.added}</strong>,
            zaktualizuję <strong>{preview.updated}</strong>, błędnych wierszy{" "}
            <strong>{preview.problems.length}</strong>. Rozdzielnik: {preview.separator}
            {preview.headerSkipped ? ", nagłówek pominięty" : ""}.
          </p>
          {preview.problems.length > 0 && (
            <div className="admin-table-wrap" style={{ marginTop: 12 }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Wiersz</th>
                    <th>Treść</th>
                    <th>Dlaczego pominięty</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.problems.map((p) => (
                    <tr key={`${p.line}-${p.reason}`}>
                      <td>{p.line}</td>
                      <td>{p.text}</td>
                      <td>{p.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {preview.entries.length > 0 && (
            <div className="admin-table-wrap" style={{ marginTop: 12 }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Numer</th>
                    <th>Kontakt</th>
                    <th>Co zrobię</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.entries.slice(0, 50).map((en) => (
                    <tr key={`${en.line}-${en.phone}`}>
                      <td>{en.phone}</td>
                      <td>{en.name}</td>
                      <td>
                        {en.action}
                        {en.fields.length > 0 ? ` (${en.fields.join(", ")})` : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.entries.length > 50 && (
                <p className="muted" style={{ margin: "8px 0 0" }}>
                  …i {preview.entries.length - 50} dalszych.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ContactsPanel() {
  const toast = useToast();
  const confirm = useConfirm();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY_CONTACT_FORM });
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminFetch<{ contacts: Contact[] }>("/admin/api/contacts");
      setContacts(r.contacts);
      setLoadErr(null);
    } catch (e) {
      setLoadErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const editContact = (c: Contact) => {
    setFormErr(null);
    setForm({
      id: c.id,
      phone: c.phoneRaw,
      firstName: c.firstName,
      lastName: c.lastName,
      note: c.note ?? "",
      promptAddendum: c.promptAddendum ?? "",
    });
  };

  const resetForm = () => {
    setFormErr(null);
    setForm({ ...EMPTY_CONTACT_FORM });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setFormErr(null);
    try {
      const body = JSON.stringify({
        phone: form.phone,
        firstName: form.firstName,
        lastName: form.lastName,
        note: form.note,
        promptAddendum: form.promptAddendum,
      });
      if (form.id) {
        await adminFetch(`/admin/api/contacts/${form.id}`, { method: "PATCH", body });
        toast.success("Zapisano zmiany");
      } else {
        await adminFetch("/admin/api/contacts", { method: "POST", body });
        toast.success("Dodano kontakt");
      }
      resetForm();
      await load();
    } catch (err) {
      setFormErr((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c: Contact) => {
    const name = `${c.firstName} ${c.lastName}`.trim() || c.phoneRaw;
    const ok = await confirm({
      title: `Usunąć kontakt „${name}"?`,
      body: `Numer ${c.phoneRaw} przestanie być rozpoznawany. Tej operacji nie można cofnąć.`,
      confirmLabel: "Usuń",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await adminFetch(`/admin/api/contacts/${c.id}`, { method: "DELETE" });
      if (form.id === c.id) resetForm();
      await load();
      toast.success("Usunięto kontakt");
    } catch (err) {
      toast.error("Nie udało się usunąć kontaktu", (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="view-stack">
      <div className="panel">
        <h3>{form.id ? "Edytuj kontakt" : "Dodaj kontakt"}</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Przypisz numer telefonu do imienia i nazwiska. Wtedy w Transkryptach i na Slacku zamiast
          samego numeru zobaczysz kto dzwonił (numer nadal jest pokazywany). Dopasowanie po ostatnich
          9 cyfrach - format zapisu (spacje, +48) nie ma znaczenia.
        </p>
        <form onSubmit={(e) => void submit(e)}>
          <div className="admin-settings-grid" style={gridCols(3)}>
            <label className="admin-field-block">
              <div>Numer telefonu</div>
              <input
                type="text"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="np. 509 891 745"
                maxLength={40}
              />
            </label>
            <label className="admin-field-block">
              <div>Imię</div>
              <input type="text" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} maxLength={120} />
            </label>
            <label className="admin-field-block">
              <div>Nazwisko</div>
              <input type="text" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} maxLength={120} />
            </label>
          </div>
          <label className="admin-field-block" style={{ marginTop: 12 }}>
            <div>Notatka (opcjonalnie)</div>
            <input
              type="text"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="np. klient, znajomy, dostawca"
              maxLength={2000}
            />
          </label>
          <label className="admin-field-block" style={{ marginTop: 12 }}>
            <div>Instrukcja dla tego rozmówcy (opcjonalnie)</div>
            <textarea
              rows={4}
              value={form.promptAddendum}
              onChange={(e) => setForm({ ...form, promptAddendum: e.target.value })}
              placeholder="np. To stały klient. Zapytaj, jak działa wdrożona automatyzacja i czy czegoś nie poprawić."
              maxLength={4000}
            />
            <p className="muted" style={{ marginTop: 6 }}>
              Doklejana do promptu tylko wtedy, gdy dzwoni ten numer — i tylko na numerach
              z włączonym rozpoznawaniem dzwoniących (zakładka Numery SIP). Napisz zwykłym
              językiem, czego oczekujesz od asystentki w rozmowie z tą osobą.
            </p>
          </label>
          {formErr && (
            <p className="field-error" style={{ marginTop: 10 }}>
              <IconAlertCircle size={14} />
              {formErr}
            </p>
          )}
          <div className="row" style={{ gap: 12, marginTop: 16 }}>
            <button type="submit" className="btn" disabled={busy}>
              {busy ? "Zapisuję..." : form.id ? "Zapisz zmiany" : "Dodaj kontakt"}
            </button>
            {form.id && (
              <button type="button" className="btn-secondary" onClick={resetForm} disabled={busy}>
                Anuluj
              </button>
            )}
          </div>
        </form>
      </div>

      <ContactsImportPanel onImported={load} />

      <div className="panel">
        <h3>Kontakty ({contacts.length})</h3>
        {loading ? (
          <LoadingState text="Ładowanie kontaktów..." />
        ) : loadErr ? (
          <EmptyState
            title="Nie udało się pobrać kontaktów"
            desc={loadErr}
            icon={<IconAlertCircle size={22} />}
            action={
              <button type="button" className="btn-secondary" onClick={() => void load()}>
                Spróbuj ponownie
              </button>
            }
          />
        ) : contacts.length === 0 ? (
          <EmptyState
            title="Brak kontaktów"
            desc="Dodaj pierwszy kontakt powyżej, aby rozpoznawać dzwoniących po nazwie."
            icon={<IconUsers size={22} />}
          />
        ) : (
          <div className="admin-table-wrap" style={{ marginTop: 16 }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Imię i nazwisko</th>
                  <th>Numer</th>
                  <th>Notatka</th>
                  <th>Instrukcja</th>
                  <th aria-label="Akcje" />
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id}>
                    <td>{`${c.firstName} ${c.lastName}`.trim() || "-"}</td>
                    <td>{c.phoneRaw}</td>
                    <td>{c.note ?? "-"}</td>
                    <td>{c.promptAddendum ? "tak" : "-"}</td>
                    <td>
                      <div className="row" style={{ gap: 8, justifyContent: "flex-end", flexWrap: "nowrap" }}>
                        <button type="button" className="btn-icon-only" aria-label={`Edytuj ${c.firstName} ${c.lastName}`.trim()} onClick={() => editContact(c)}>
                          <IconEdit />
                        </button>
                        <button type="button" className="btn-icon-only" aria-label={`Usuń ${c.firstName} ${c.lastName}`.trim()} onClick={() => void remove(c)} disabled={busy}>
                          <IconTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- Numery SIP */

type SipTrunk = {
  id: string;
  slug: string;
  label: string;
  number: string;
  sipUsername: string;
  hasPassword: boolean;
  registrarHost: string;
  registrarPort: number;
  localPort: number;
  fromDomain: string | null;
  identifyIps: string;
  codecs: string;
  openingNote: string | null;
  openingLiteral: boolean;
  callerContextTemplate: string | null;
  promptAddendum: string | null;
  callerContextCrmEnabled: boolean;
  callerContextContactsEnabled: boolean;
  enabled: boolean;
  outboundDefault: boolean;
  registrationStatus: string | null;
  registrationCheckedAt: string | null;
};

const EMPTY_TRUNK_FORM = {
  id: null as string | null,
  slug: "",
  label: "",
  number: "",
  sipUsername: "",
  sipPassword: "",
  registrarHost: "",
  registrarPort: "5060",
  fromDomain: "",
  identifyIps: "",
  codecs: "alaw,ulaw",
  openingNote: "",
  openingLiteral: false,
  callerContextTemplate: "",
  promptAddendum: "",
  callerContextCrmEnabled: false,
  callerContextContactsEnabled: false,
  localPort: 45060,
  enabled: true,
  outboundDefault: false,
};

/**
 * Podstawienia, które biorą się z danych rozmówcy. Przy obu źródłach rozpoznawania odznaczonych
 * wszystkie wyjdą puste — panel ma o tym uprzedzić, zanim ktoś zapisze urwane zdanie.
 */
const CALLER_TAGS = [
  "numer",
  "imie",
  "nazwisko",
  "imieNazwisko",
  "notatka",
  "instrukcja",
  "nazwaCrm",
  "opisStatusuCrm",
  "statusAiCrm",
  "kontekstCrm",
  "interwencjaCrm",
  "opiekun",
];

/** Łapie i zwykłe {{imie}}, i formy blokowe {{#imie}} / {{^imie}} / {{/imie}}. */
const CALLER_TAG_PATTERN = new RegExp(
  "[{][{][#^/]?(?:" + CALLER_TAGS.join("|") + ")[}][}]",
);

function usesCallerTag(text: string): boolean {
  return CALLER_TAG_PATTERN.test(text);
}

/** Zakładki okna edycji: dane operatora osobno, zachowanie asystentki osobno. */
type TrunkTab = "tech" | "behavior";

const TRUNK_TABS = [
  { value: "tech", label: "Ustawienia techniczne" },
  { value: "behavior", label: "Zachowanie asystentki" },
];

/** „Registered" → zielona pigułka, „Rejected" → czerwona, reszta → neutralna. */
function TrunkStatusPill({ trunk }: { trunk: SipTrunk }) {
  if (!trunk.enabled) return <span className="muted">wyłączony</span>;
  const status = trunk.registrationStatus;
  if (!status) return <span className="muted">nieznany</span>;
  const cls =
    status === "Registered"
      ? "status-pill status-pill--ok"
      : status === "Rejected"
        ? "status-pill status-pill--err"
        : "status-pill status-pill--warn";
  const label =
    status === "Registered"
      ? "zarejestrowany"
      : status === "Rejected"
        ? "odrzucony (hasło/login)"
        : status === "Unregistered"
          ? "niezarejestrowany"
          : status;
  return (
    <span className={cls}>
      <span className="status-pill__dot" />
      {label}
    </span>
  );
}

function SipTrunksPanel() {
  const toast = useToast();
  const confirm = useConfirm();
  const [trunks, setTrunks] = useState<SipTrunk[]>([]);
  const [secretKeyConfigured, setSecretKeyConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY_TRUNK_FORM });
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  // Czy okno edycji stoi na wierzchu. O tym, czy dodajemy czy zmieniamy, dalej rozstrzyga form.id.
  const [editorOpen, setEditorOpen] = useState(false);
  // Odbicie formularza z chwili otwarcia - po nim poznajemy, czy przy zamykaniu jest co stracić.
  const [baseline, setBaseline] = useState({ ...EMPTY_TRUNK_FORM });
  // Która zakładka okna jest na wierzchu. CELOWO poza `form`: requestClose porównuje form
  // z baseline pole po polu, więc zakładka w formularzu liczyłaby się jako niezapisana zmiana
  // i samo jej przełączenie wywoływałoby pytanie „porzucić zmiany?".
  const [tab, setTab] = useState<TrunkTab>("tech");
  // Rdzeń identyfikatorów wiążących zakładkę z jej panelem - patrz segTabId/segPanelId.
  const tabIdBase = useId();
  const [advOpen, setAdvOpen] = useState(false);
  // Trwa pytanie „porzucić zmiany?" - patrz zapora w onClose okna.
  const [discardAsk, setDiscardAsk] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminFetch<{ trunks: SipTrunk[]; secretKeyConfigured: boolean }>(
        "/admin/api/sip-trunks",
      );
      setTrunks(r.trunks);
      setSecretKeyConfigured(r.secretKeyConfigured);
      setLoadErr(null);
    } catch (e) {
      setLoadErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const resetForm = () => {
    setFormErr(null);
    setForm({ ...EMPTY_TRUNK_FORM });
    setBaseline({ ...EMPTY_TRUNK_FORM });
  };

  const closeEditor = () => {
    setEditorOpen(false);
    resetForm();
  };

  /** Zamknięcie na życzenie (Escape, krzyżyk, kliknięcie w tło, Anuluj) - z pytaniem, gdy jest co stracić. */
  const requestClose = async () => {
    // Porównanie pole po polu, a nie po JSON - żeby nie zależeć od kolejności kluczy w obiekcie.
    const untouched = (Object.keys(form) as Array<keyof typeof form>).every(
      (k) => form[k] === baseline[k],
    );
    if (untouched) {
      closeEditor();
      return;
    }
    setDiscardAsk(true);
    const ok = await confirm({
      title: "Porzucić niezapisane zmiany?",
      body: "Wpisane dane przepadną, a numer zostanie taki, jaki był.",
      confirmLabel: "Porzuć",
      danger: true,
    });
    setDiscardAsk(false);
    if (ok) closeEditor();
  };

  const openAdd = () => {
    setFormErr(null);
    setForm({ ...EMPTY_TRUNK_FORM });
    setBaseline({ ...EMPTY_TRUNK_FORM });
    // Przy nowym numerze najpierw trzeba wpisać dane operatora.
    setTab("tech");
    setAdvOpen(false);
    setEditorOpen(true);
  };

  const openEdit = (t: SipTrunk, startTab: TrunkTab = "tech") => {
    const next = {
      id: t.id,
      slug: t.slug,
      label: t.label,
      number: t.number,
      sipUsername: t.sipUsername,
      sipPassword: "",
      registrarHost: t.registrarHost,
      registrarPort: String(t.registrarPort),
      fromDomain: t.fromDomain ?? "",
      identifyIps: t.identifyIps,
      codecs: t.codecs,
      openingNote: t.openingNote ?? "",
      openingLiteral: t.openingLiteral,
      callerContextTemplate: t.callerContextTemplate ?? "",
      promptAddendum: t.promptAddendum ?? "",
      callerContextCrmEnabled: t.callerContextCrmEnabled,
      callerContextContactsEnabled: t.callerContextContactsEnabled,
      localPort: t.localPort ?? 45060,
      enabled: t.enabled,
      outboundDefault: t.outboundDefault,
    };
    setFormErr(null);
    setForm(next);
    setBaseline(next);
    setTab(startTab);
    // Rozwinięte tylko wtedy, gdy naprawdę coś tam jest - inaczej ustawienie schowałoby się po cichu.
    setAdvOpen(Boolean(next.callerContextTemplate.trim() || next.promptAddendum.trim()));
    setEditorOpen(true);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setFormErr(null);
    try {
      const body = JSON.stringify({
        slug: form.slug,
        label: form.label,
        number: form.number,
        sipUsername: form.sipUsername,
        sipPassword: form.sipPassword,
        registrarHost: form.registrarHost,
        registrarPort: form.registrarPort,
        fromDomain: form.fromDomain,
        identifyIps: form.identifyIps,
        codecs: form.codecs,
        openingNote: form.openingNote,
        openingLiteral: form.openingLiteral,
        callerContextTemplate: form.callerContextTemplate,
        promptAddendum: form.promptAddendum,
        callerContextCrmEnabled: form.callerContextCrmEnabled,
        callerContextContactsEnabled: form.callerContextContactsEnabled,
        localPort: form.localPort,
        enabled: form.enabled,
        outboundDefault: form.outboundDefault,
      });
      if (form.id) {
        await adminFetch(`/admin/api/sip-trunks/${form.id}`, { method: "PATCH", body });
        toast.success("Zapisano numer", "Konfiguracja Asteriska została przeładowana.");
      } else {
        await adminFetch("/admin/api/sip-trunks", { method: "POST", body });
        toast.success("Dodano numer", "Konfiguracja Asteriska została przeładowana.");
      }
      closeEditor();
      await refreshStatuses();
    } catch (err) {
      setFormErr((err as Error).message);
      // Serwer odrzuca wyłącznie pola techniczne (nazwa, numer, login, serwer, porty, hasło),
      // więc bez tego skoku komunikat w stopce wskazywałby na pole schowane w drugiej zakładce.
      // Po nieudanym zapisie fokus potrafi wypaść z okna na <body>, czyli poza jego pułapkę Tab -
      // i to na dwa sposoby. Raz: „Zapisz zmiany" gaśnie na czas zapisu (disabled), a przeglądarka
      // zabiera fokus wyłączonemu elementowi. Dwa: zakładka zachowania zaraz zniknie z DOM razem
      // z polem, które fokus trzymało (Enter na polu wyboru wysyła formularz). W obu razach
      // sadzamy fokus na zakładce technicznej - tej, na którą i tak przeskakujemy.
      const active = document.activeElement;
      const leavingPanel = document.getElementById(segPanelId(tabIdBase, "behavior"));
      if (active === document.body || leavingPanel?.contains(active)) {
        document.getElementById(segTabId(tabIdBase, "tech"))?.focus();
      }
      setTab("tech");
    } finally {
      setBusy(false);
    }
  };

  const toggleEnabled = async (t: SipTrunk) => {
    setBusy(true);
    try {
      const r = await adminFetch<{ reloadError: string | null }>(
        `/admin/api/sip-trunks/${t.id}/toggle`,
        { method: "POST", body: JSON.stringify({ enabled: !t.enabled }) },
      );
      if (r.reloadError) toast.error("Asterisk nie przeładował konfiguracji", r.reloadError);
      await refreshStatuses();
    } catch (err) {
      toast.error("Nie udało się przełączyć numeru", (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t: SipTrunk) => {
    const ok = await confirm({
      title: `Usunąć numer „${t.label}"?`,
      body: `Numer ${t.number} przestanie się rejestrować i nie będzie odbierał połączeń. Tej operacji nie można cofnąć.`,
      confirmLabel: "Usuń",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await adminFetch(`/admin/api/sip-trunks/${t.id}`, { method: "DELETE" });
      if (form.id === t.id) closeEditor();
      await load();
      toast.success("Usunięto numer");
    } catch (err) {
      toast.error("Nie udało się usunąć numeru", (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const refreshStatuses = useCallback(async () => {
    try {
      const r = await adminFetch<{ trunks: SipTrunk[]; error: string | null }>(
        "/admin/api/sip-trunks/status",
      );
      setTrunks(r.trunks);
      if (r.error) toast.error("Nie udało się odczytać statusów z Asteriska", r.error);
    } catch (err) {
      toast.error("Nie udało się odświeżyć statusów", (err as Error).message);
    }
  }, [toast]);

  const applyAll = async () => {
    setBusy(true);
    try {
      const r = await adminFetch<{
        applied: string[];
        removed: string[];
        skipped: string[];
        reloadError: string | null;
      }>("/admin/api/sip-trunks/apply", { method: "POST" });
      if (r.reloadError) {
        toast.error("Asterisk nie przeładował konfiguracji", r.reloadError);
      } else if (r.skipped.length > 0) {
        toast.error(
          "Część numerów pominięto",
          `Nie udało się odszyfrować hasła dla: ${r.skipped.join(", ")}. Wpisz je ponownie.`,
        );
      } else {
        toast.success("Zastosowano konfigurację", `Czynne numery: ${r.applied.join(", ") || "brak"}`);
      }
      await refreshStatuses();
    } catch (err) {
      toast.error("Nie udało się zastosować konfiguracji", (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const detectIps = async () => {
    if (!form.registrarHost.trim()) {
      setFormErr("Najpierw podaj adres serwera operatora.");
      return;
    }
    setBusy(true);
    try {
      const r = await adminFetch<{ addresses: string[]; srvTargets: string[]; warnings: string[] }>(
        "/admin/api/sip-trunks/resolve",
        { method: "POST", body: JSON.stringify({ registrarHost: form.registrarHost }) },
      );
      if (r.addresses.length > 0) {
        setForm((f) => ({ ...f, identifyIps: r.addresses.join(", ") }));
        toast.success(
          `Znaleziono ${r.addresses.length} adres(y)`,
          r.srvTargets.length > 0 ? `Z rekordów SRV: ${r.srvTargets.join(", ")}` : undefined,
        );
      }
      if (r.warnings.length > 0) toast.error("Uwaga przy wykrywaniu adresów", r.warnings.join(" "));
    } catch (err) {
      toast.error("Nie udało się wykryć adresów", (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const activeCount = trunks.filter((t) => t.enabled).length;

  return (
    <div className="view-stack">
      {!secretKeyConfigured && (
        <ErrorBanner message="Brak klucza szyfrującego hasła SIP. Ustaw SECRET_ENCRYPTION_KEY w pliku .env na serwerze (wygeneruj: openssl rand -base64 32) i zrestartuj aplikację - bez tego nie zapiszesz hasła." />
      )}

      <div className="panel">
        <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <h3 style={{ margin: 0 }}>
              Numery ({trunks.length}, czynne: {activeCount})
            </h3>
            <p className="muted" style={{ margin: "4px 0 0", maxWidth: "70ch" }}>
              Dane z panelu operatora. Po zapisaniu aplikacja sama generuje konfigurację Asteriska
              i przeładowuje łącza - nie trzeba niczego robić na serwerze. Numerów może być dowolnie
              wiele i wszystkie zaznaczone jako czynne działają równocześnie.
            </p>
          </div>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="btn-secondary" onClick={() => void refreshStatuses()} disabled={busy}>
              Odśwież statusy
            </button>
            <button type="button" className="btn-secondary" onClick={() => void applyAll()} disabled={busy}>
              Zastosuj i przeładuj
            </button>
            <button type="button" className="btn" onClick={openAdd} disabled={busy}>
              <IconPlus size={14} />
              Dodaj numer
            </button>
          </div>
        </div>

        {loading ? (
          <LoadingState text="Ładowanie numerów..." />
        ) : loadErr ? (
          <EmptyState
            title="Nie udało się pobrać numerów"
            desc={loadErr}
            icon={<IconAlertCircle size={22} />}
            action={
              <button type="button" className="btn-secondary" onClick={() => void load()}>
                Spróbuj ponownie
              </button>
            }
          />
        ) : trunks.length === 0 ? (
          <EmptyState
            title="Brak numerów"
            desc="Dane pierwszego numeru znajdziesz w panelu swojego operatora VoIP."
            icon={<IconPhoneCall size={22} />}
            action={
              <button type="button" className="btn" onClick={openAdd}>
                <IconPlus size={14} />
                Dodaj numer
              </button>
            }
          />
        ) : (
          <div className="admin-table-wrap" style={{ marginTop: 16 }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Czynny</th>
                  <th>Nazwa</th>
                  <th>Numer</th>
                  <th>Zachowanie</th>
                  <th>Konto SIP</th>
                  <th>Rejestracja</th>
                  <th aria-label="Akcje" />
                </tr>
              </thead>
              <tbody>
                {trunks.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <Checkbox
                        checked={t.enabled}
                        disabled={busy}
                        onChange={() => void toggleEnabled(t)}
                        ariaLabel={`Numer ${t.label} czynny`}
                      />
                    </td>
                    <td>
                      {t.label}
                      {t.outboundDefault && (
                        <span className="muted" style={{ marginLeft: 6 }}>
                          (wychodzące)
                        </span>
                      )}
                    </td>
                    <td>{t.number}</td>
                    <td>
                      {t.openingNote?.trim() ? (
                        t.openingLiteral ? (
                          "własne, słowo w słowo"
                        ) : (
                          "własne powitanie"
                        )
                      ) : (
                        <span className="muted">wbudowane</span>
                      )}
                      {(t.callerContextCrmEnabled || t.callerContextContactsEnabled) && (
                        <span className="muted">
                          {" "}
                          · rozpoznaje:{" "}
                          {t.callerContextCrmEnabled && t.callerContextContactsEnabled
                            ? "CRM i Kontakty"
                            : t.callerContextCrmEnabled
                              ? "CRM"
                              : "Kontakty"}
                        </span>
                      )}
                    </td>
                    <td>
                      {t.sipUsername}
                      <span className="muted">@{t.fromDomain ?? t.registrarHost}</span>
                      {!t.hasPassword && <span className="muted"> · brak hasła</span>}
                    </td>
                    <td>
                      <TrunkStatusPill trunk={t} />
                    </td>
                    <td>
                      {/* Dwie ikonki zamiast jednego ołówka: każda otwiera to samo okno, ale od razu
                          na swojej zakładce. Suwaki to ta sama ikonka co „Ustawienia techniczne"
                          w nawigacji. Same ikonki są mniej oczywiste niż ołówek - stąd title. */}
                      <div className="row" style={{ gap: 8, justifyContent: "flex-end", flexWrap: "nowrap" }}>
                        <button
                          type="button"
                          className="btn-icon-only"
                          aria-label={`Ustawienia techniczne: ${t.label}`}
                          title="Ustawienia techniczne (numer, hasło, serwer)"
                          onClick={() => openEdit(t, "tech")}
                        >
                          <IconSliders />
                        </button>
                        <button
                          type="button"
                          className="btn-icon-only"
                          aria-label={`Zachowanie asystentki: ${t.label}`}
                          title="Zachowanie asystentki (powitanie, rozpoznawanie dzwoniących)"
                          onClick={() => openEdit(t, "behavior")}
                        >
                          <IconChat />
                        </button>
                        <button type="button" className="btn-icon-only" aria-label={`Usuń ${t.label}`} onClick={() => void remove(t)} disabled={busy}>
                          <IconTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        open={editorOpen}
        onClose={() => {
          // Zapora na Escape: Modal słucha klawiatury na całym dokumencie i nie zatrzymuje
          // zdarzenia, więc bez niej jedno naciśnięcie trafiłoby i w pytanie „porzucić zmiany?",
          // i w to okno pod spodem - pytanie wracałoby w kółko.
          if (busy || discardAsk) return;
          void requestClose();
        }}
        labelledBy="trunk-modal-title"
        panelClassName="modal__panel trunk-modal__panel"
        initialFocusSelector="[data-trunk-first-field]"
      >
        <form className="trunk-modal__form" onSubmit={(e) => void submit(e)}>
          <div className="trunk-modal__header">
            <div>
              {/* Nazwa i numer w tytule, bo jako pola siedzą w zakładce technicznej - inaczej
                  z zakładki „Zachowanie" nie byłoby widać, którego numeru dotyczy edycja. */}
              <h2 id="trunk-modal-title" className="modal__title">
                {form.id
                  ? `Edytuj numer: ${form.label || form.slug}${form.number ? ` · ${form.number}` : ""}`
                  : "Dodaj numer VoIP"}
              </h2>
              <p className="muted" style={{ margin: "4px 0 0" }}>
                Po zapisaniu aplikacja sama generuje konfigurację Asteriska i przeładowuje łącza.
              </p>
            </div>
            <button
              type="button"
              className="btn-icon-only"
              onClick={() => {
                if (!busy) void requestClose();
              }}
              aria-label="Zamknij"
            >
              <IconX size={16} />
            </button>
          </div>

          <div className="trunk-modal__body">
            <SegmentedControl
              idBase={tabIdBase}
              options={TRUNK_TABS}
              value={tab}
              onChange={(v) => setTab(v as TrunkTab)}
              ariaLabel="Sekcje ustawień numeru"
            />

            {/* Zakładka spoza wierzchu jest odmontowana, nie ukryta CSS-em - tak samo, jak dotąd
                działały zwinięte sekcje Collapse. Wartości siedzą w `form`, a nie w polach,
                więc zapis wysyła komplet niezależnie od tego, którą zakładkę widać. */}
            {tab === "tech" ? (
              <div
                className="reveal"
                role="tabpanel"
                id={segPanelId(tabIdBase, "tech")}
                aria-labelledby={segTabId(tabIdBase, "tech")}
              >
                <div className="admin-settings-grid" style={gridCols(3)}>
                  <label className="admin-field-block">
                    <div>Nazwa (dla Ciebie)</div>
                    <input
                      type="text"
                      data-trunk-first-field
                      value={form.label}
                      onChange={(e) => setForm({ ...form, label: e.target.value })}
                      placeholder="np. Plus VPBX"
                      maxLength={80}
                    />
                  </label>
                  <label className="admin-field-block">
                    <div>Numer telefonu</div>
                    <input
                      type="text"
                      value={form.number}
                      onChange={(e) => setForm({ ...form, number: e.target.value })}
                      placeholder="np. 718890509"
                      maxLength={40}
                    />
                  </label>
                  <label className="admin-field-block">
                    <div>Identyfikator techniczny</div>
                    <input
                      type="text"
                      value={form.slug}
                      onChange={(e) => setForm({ ...form, slug: e.target.value })}
                      placeholder="np. plus"
                      maxLength={32}
                      disabled={form.id != null}
                    />
                  </label>
                </div>

                <div className="admin-settings-grid" style={gridCols(3, { marginTop: 12 })}>
                  <label className="admin-field-block">
                    <div>Login SIP</div>
                    <input
                      type="text"
                      value={form.sipUsername}
                      onChange={(e) => setForm({ ...form, sipUsername: e.target.value })}
                      placeholder="np. 718890509"
                      maxLength={255}
                    />
                  </label>
                  <label className="admin-field-block">
                    <div>{form.id ? "Nowe hasło SIP (puste = bez zmiany)" : "Hasło SIP"}</div>
                    <input
                      type="password"
                      value={form.sipPassword}
                      onChange={(e) => setForm({ ...form, sipPassword: e.target.value })}
                      autoComplete="new-password"
                      placeholder={form.id ? "bez zmiany" : ""}
                      maxLength={255}
                    />
                  </label>
                  <label className="admin-field-block">
                    <div>Serwer operatora</div>
                    <input
                      type="text"
                      value={form.registrarHost}
                      onChange={(e) => setForm({ ...form, registrarHost: e.target.value })}
                      placeholder="np. vpbx.plus.pl"
                      maxLength={255}
                    />
                  </label>
                </div>

                <div className="admin-settings-grid" style={gridCols(3, { marginTop: 12 })}>
                  <label className="admin-field-block">
                    <div>Port</div>
                    <input
                      type="text"
                      value={form.registrarPort}
                      onChange={(e) => setForm({ ...form, registrarPort: e.target.value })}
                      placeholder="5060"
                      maxLength={5}
                    />
                  </label>
                  <label className="admin-field-block">
                    <div>Domena w nagłówku From (opcjonalnie)</div>
                    <input
                      type="text"
                      value={form.fromDomain}
                      onChange={(e) => setForm({ ...form, fromDomain: e.target.value })}
                      placeholder="puste = adres serwera"
                      maxLength={255}
                    />
                  </label>
                  <label className="admin-field-block">
                    <div>Kodeki</div>
                    <input
                      type="text"
                      value={form.codecs}
                      onChange={(e) => setForm({ ...form, codecs: e.target.value })}
                      placeholder="alaw,ulaw"
                      maxLength={200}
                    />
                  </label>
                </div>

                <label className="admin-field-block" style={{ marginTop: 12 }}>
                  <div>Adresy IP operatora (połączenia przychodzące)</div>
                  <p className="muted" style={{ margin: "4px 0 8px" }}>
                    Po przecinku. Dzięki nim Asterisk rozpoznaje, że połączenie przyszło od tego
                    operatora. Puste = przyjmujemy tylko połączenia po zarejestrowanym łączu.
                  </p>
                  <div className="row" style={{ gap: 8, alignItems: "center" }}>
                    <input
                      type="text"
                      value={form.identifyIps}
                      onChange={(e) => setForm({ ...form, identifyIps: e.target.value })}
                      placeholder="np. 212.2.101.70, 212.2.101.20"
                      style={{ flex: 1 }}
                      maxLength={1000}
                    />
                    <button type="button" className="btn-secondary" onClick={() => void detectIps()} disabled={busy}>
                      Wykryj z DNS
                    </button>
                  </div>
                </label>

                <label className="admin-field-block" style={{ marginTop: 12 }}>
                  <div>Port lokalny (rozróżnianie numerów u jednego operatora)</div>
                  <p className="muted" style={{ margin: "4px 0 8px" }}>
                    Port, którym ten numer melduje się u operatora. Operator dostarcza połączenia pod
                    adres z rejestracji, więc <strong>dwa numery u tego samego operatora muszą mieć różne
                    porty</strong> - inaczej ich połączeń nie da się odróżnić i oba dostaną to samo
                    powitanie. Lista jest stała, bo Asterisk ma przygotowany transport tylko dla tych portów.
                  </p>
                  <select
                    value={String(form.localPort)}
                    onChange={(e) => setForm({ ...form, localPort: Number(e.target.value) })}
                  >
                    {[45060, 45061, 45062, 45063, 45064].map((p) => (
                      <option key={p} value={p}>
                        {p}
                        {p === 45060 ? " (domyślny)" : ""}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="row" style={{ gap: 20, marginTop: 18, flexWrap: "wrap" }}>
                  <Checkbox checked={form.enabled} onChange={(v) => setForm({ ...form, enabled: v })}>
                    Czynny (rejestruj i odbieraj połączenia)
                  </Checkbox>
                  <Checkbox
                    checked={form.outboundDefault}
                    onChange={(v) => setForm({ ...form, outboundDefault: v })}
                  >
                    Domyślny do połączeń wychodzących
                  </Checkbox>
                </div>
                <p className="muted" style={{ marginTop: 6 }}>
                  Przełączenie do człowieka wychodzi tym numerem, którym przyszła rozmowa. Numer domyślny
                  jest używany tylko wtedy, gdy rozmowa nie przyszła żadnym łączem - na przykład w teście
                  z zakładki „Rozmowa".
                </p>
              </div>
            ) : (
              <div
                className="reveal admin-field-block"
                role="tabpanel"
                id={segPanelId(tabIdBase, "behavior")}
                aria-labelledby={segTabId(tabIdBase, "behavior")}
              >
                <p className="muted" style={{ margin: "0 0 12px" }}>
                  Tu ustawiasz, JAK asystentka ma się zachować na tym numerze. Co WIE, bierze się
                  z master promptu w „Ustawieniach". Puste pola = zachowanie wbudowane. Dzięki temu
                  jeden numer może być publiczną infolinią, a drugi celem przekierowania z komórki -
                  i asystentka wita się na każdym inaczej.
                </p>
                <label className="admin-field-block">
                  <div>Instrukcja początkowa dla tego numeru</div>
                  <p className="muted" style={{ margin: "4px 0 8px" }}>
                    Napisz własnymi słowami, jak asystentka ma zacząć rozmowę przychodzącą na ten numer
                    - to wskazówka dla niej, a nie tekst do przeczytania, więc może użyć imienia
                    dzwoniącego i ułożyć zdanie sama. Działają te same podstawienia co w sekcji „kto
                    dzwoni", np. <code>{"{{#imie}}przywitaj się z {{imie}}{{/imie}}"}</code>. Puste =
                    instrukcja wbudowana.
                  </p>
                  <textarea
                    rows={6}
                    data-trunk-first-field
                    value={form.openingNote}
                    onChange={(e) => setForm({ ...form, openingNote: e.target.value })}
                    placeholder="np. Najpierw przywitaj rozmówcę, potem powiedz, że jesteś asystentką Mateusza, a dopiero na końcu, że nie może teraz odebrać."
                    maxLength={3000}
                  />
                </label>
                {usesCallerTag(form.openingNote) &&
                  !form.callerContextCrmEnabled &&
                  !form.callerContextContactsEnabled && (
                    <p className="muted" style={{ margin: "-4px 0 0" }}>
                      Oba źródła rozpoznawania są wyłączone, więc ten tag zostanie pusty, a zdanie
                      wokół niego się urwie.
                    </p>
                  )}
                <div className="admin-field-block" style={{ marginTop: 12 }}>
                  <Checkbox
                    checked={form.openingLiteral}
                    onChange={(v) => setForm({ ...form, openingLiteral: v })}
                  >
                    Powiedz dokładnie tak, jak napisane (słowo w słowo)
                  </Checkbox>
                  <p className="muted" style={{ marginTop: 6 }}>
                    Zaznacz, jeśli powitanie ma zabrzmieć co do słowa tak, jak wpisane wyżej. Wtedy tekst
                    przestaje być wskazówką: asystentka odczyta go bez imienia dzwoniącego i bez
                    kontekstu, a zadziała znacznik <code>{"{{filler}}"}</code> (dźwięk „zastanawiam się"
                    między fragmentami). Podstawienia w tym trybie nie działają.
                  </p>
                </div>
                <div className="admin-field-block" style={{ marginTop: 12 }}>
                  <Checkbox
                    checked={form.callerContextCrmEnabled}
                    onChange={(v) => setForm({ ...form, callerContextCrmEnabled: v })}
                  >
                    Rozpoznawaj dzwoniących z CRM
                  </Checkbox>
                  <p className="muted" style={{ marginTop: 6 }}>
                    Asystentka pozna imię osoby, jeśli jej numer jest w danych z CRM, a razem z nim
                    etap sprawy, na czym stanęło i co o niej wiemy.
                  </p>
                </div>
                <div className="admin-field-block" style={{ marginTop: 12 }}>
                  <Checkbox
                    checked={form.callerContextContactsEnabled}
                    onChange={(v) => setForm({ ...form, callerContextContactsEnabled: v })}
                  >
                    Rozpoznawaj dzwoniących z Kontaktów
                  </Checkbox>
                  <p className="muted" style={{ marginTop: 6 }}>
                    Asystentka pozna imię z zakładki „Kontakty", a także notatkę i instrukcję
                    wpisaną tam ręcznie przy tej osobie. Gdy numer jest w obu miejscach, imię idzie
                    z CRM.
                  </p>
                </div>

                {/* Rozwijane samo, gdy numer naprawdę czegoś tu używa (patrz openEdit) - inaczej
                    ustawiona wartość schowałaby się po cichu za zwiniętym nagłówkiem. */}
                <button
                  type="button"
                  className="disclosure"
                  aria-expanded={advOpen}
                  onClick={() => setAdvOpen((v) => !v)}
                >
                  <IconChevronDown size={16} className="disclosure__chevron" />
                  Zaawansowane
                  <span className="disclosure__hint">sekcja „kto dzwoni", dodatkowa instrukcja</span>
                </button>
                <Collapse open={advOpen}>
                  <label className="admin-field-block" style={{ marginTop: 12 }}>
                    <div>Sekcja „kto dzwoni" dla tego numeru</div>
                    <p className="muted" style={{ margin: "4px 0 8px" }}>
                      Doklejana na sam koniec promptu, gdy wyżej jest włączone którekolwiek
                      z rozpoznawań dzwoniących.
                      Puste = wbudowany szablon. Dostępne podstawienia: <code>{"{{numer}}"}</code>,{" "}
                      <code>{"{{imie}}"}</code>, <code>{"{{nazwisko}}"}</code>,{" "}
                      <code>{"{{imieNazwisko}}"}</code>, <code>{"{{notatka}}"}</code>,{" "}
                      <code>{"{{instrukcja}}"}</code>, <code>{"{{kontekstCrm}}"}</code>,{" "}
                      <code>{"{{opisStatusuCrm}}"}</code>, <code>{"{{statusAiCrm}}"}</code>,{" "}
                      <code>{"{{interwencjaCrm}}"}</code>, <code>{"{{opiekun}}"}</code>. Fragment
                      pokazywany tylko gdy dana wartość istnieje - zapisz jako{" "}
                      <code>{"{{#imie}}...{{/imie}}"}</code>, a wariant na jej brak jako{" "}
                      <code>{"{{^imie}}...{{/imie}}"}</code> - dzięki temu przy nieznanym numerze
                      asystentka nie zgaduje imienia.
                    </p>
                    <textarea
                      rows={6}
                      value={form.callerContextTemplate}
                      onChange={(e) => setForm({ ...form, callerContextTemplate: e.target.value })}
                      placeholder="Puste = wbudowany szablon (numer + imię, kontekst z CRM, instrukcja kontaktu)."
                      maxLength={4000}
                    />
                  </label>
                  <label className="admin-field-block" style={{ marginTop: 12 }}>
                    <div>Dodatkowa instrukcja dla tego numeru</div>
                    <p className="muted" style={{ margin: "4px 0 8px" }}>
                      Doklejana na koniec promptu głównego tylko w rozmowach na ten numer, więc ma
                      pierwszeństwo nad ogólnymi zasadami. Puste = bez doklejki.
                    </p>
                    <textarea
                      rows={4}
                      value={form.promptAddendum}
                      onChange={(e) => setForm({ ...form, promptAddendum: e.target.value })}
                      placeholder="np. Rozmówca dzwonił bezpośrednio do Mateusza i nie zastał go. Ty odbierasz i prowadzisz rozmowę."
                      maxLength={4000}
                    />
                  </label>
                </Collapse>
              </div>
            )}
          </div>

          <div className="trunk-modal__footer">
            {formErr && (
              <p className="field-error" style={{ margin: 0, marginRight: "auto" }}>
                <IconAlertCircle size={14} />
                {formErr}
              </p>
            )}
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                if (!busy) void requestClose();
              }}
              disabled={busy}
            >
              Anuluj
            </button>
            <button type="submit" className="btn" disabled={busy}>
              {busy ? "Zapisuję..." : form.id ? "Zapisz zmiany" : "Dodaj numer"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

/* ------------------------------------------------------------------ Panel */

const NAV: (NavItem & { id: TabId })[] = [
  { id: "settings", label: "Ustawienia", icon: <IconSettings /> },
  { id: "settingsTech", label: "Ustawienia techniczne", icon: <IconSliders /> },
  { id: "sipTrunks", label: "Numery SIP", icon: <IconPhoneCall /> },
  { id: "contacts", label: "Kontakty", icon: <IconUsers /> },
  { id: "crmContacts", label: "Klienci z CRM", icon: <IconUsers /> },
  { id: "transcripts", label: "Transkrypty", icon: <IconFileText /> },
  { id: "call", label: "Rozmowa", icon: <IconPhone /> },
  { id: "chat", label: "Chat testowy", icon: <IconChat /> },
  { id: "logs", label: "Logi", icon: <IconTerminal /> },
];

const PAGE_META: Record<TabId, { title: string; sub: string }> = {
  settings: { title: "Ustawienia", sub: "Podstawowa konfiguracja asystentki. Zmiany obowiązują od nowych połączeń." },
  settingsTech: { title: "Ustawienia techniczne", sub: "Modele, niezawodność, retencja i integracje. Zmiany obowiązują od nowych połączeń." },
  sipTrunks: {
    title: "Numery SIP",
    sub: "Numery VoIP, na których odbiera asystentka. Zaznaczone działają równocześnie.",
  },
  contacts: { title: "Kontakty", sub: "Przypisz numery telefonów do nazw, aby rozpoznawać dzwoniących." },
  crmContacts: {
    title: "Klienci z CRM",
    sub: "Katalog pobierany z CRM. Asystentka rozpoznaje po nim dzwoniących; własną instrukcję dopiszesz po przeniesieniu do Kontaktów.",
  },
  transcripts: { title: "Transkrypty", sub: "Historia rozmów: podgląd, nagrania, notatki i audyty." },
  call: { title: "Rozmowa", sub: "Przetestuj asystentkę głosowo przez przeglądarkę." },
  chat: { title: "Chat testowy", sub: "Sprawdź odpowiedzi asystentki tekstowo, bez połączenia." },
  logs: { title: "Logi", sub: "Zapytania LLM, zużycie i koszty oraz logi systemowe." },
};

function AdminPanel({
  onToggleTheme,
  onLogout,
}: {
  // Bez `theme` — ikona motywu w powłoce przełącza się przez CSS (atrybut data-theme
  // na <html>), więc panel nie potrzebuje tej wartości.
  onToggleTheme: () => void;
  onLogout: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const [tab, setTab] = useState<TabId>("settings");
  const [err, setErr] = useState<string | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [settingsErr, setSettingsErr] = useState<string | null>(null);
  const [liveModels, setLiveModels] = useState<ModelOpt[]>([]);
  const [chatModels, setChatModels] = useState<ModelOpt[]>([]);
  const [geminiVoices, setGeminiVoices] = useState<VoiceOpt[]>([]);
  const [saving, setSaving] = useState(false);

  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatAuditTurns, setChatAuditTurns] = useState<ChatTurnAudit[]>([]);
  const [chatSessionStartedAt, setChatSessionStartedAt] = useState<string | null>(null);
  const [chatClientSessionId, setChatClientSessionId] = useState<string | null>(null);
  const [chatAuditLoading, setChatAuditLoading] = useState(false);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(CHAT_AUDIT_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        chatHistory?: ChatMessage[];
        chatAuditTurns?: ChatTurnAudit[];
        chatSessionStartedAt?: string;
        chatClientSessionId?: string;
      };
      if (parsed.chatHistory) setChatHistory(parsed.chatHistory);
      if (parsed.chatAuditTurns) setChatAuditTurns(parsed.chatAuditTurns);
      if (parsed.chatSessionStartedAt) setChatSessionStartedAt(parsed.chatSessionStartedAt);
      if (parsed.chatClientSessionId) setChatClientSessionId(parsed.chatClientSessionId);
    } catch {
      /* ignore corrupt session storage */
    }
  }, []);

  useEffect(() => {
    if (chatHistory.length === 0 && chatAuditTurns.length === 0) {
      sessionStorage.removeItem(CHAT_AUDIT_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(
      CHAT_AUDIT_STORAGE_KEY,
      JSON.stringify({ chatHistory, chatAuditTurns, chatSessionStartedAt, chatClientSessionId }),
    );
  }, [chatHistory, chatAuditTurns, chatSessionStartedAt, chatClientSessionId]);

  const ensureChatSession = (): { clientSessionId: string; startedAt: string } => {
    if (chatClientSessionId && chatSessionStartedAt) {
      return { clientSessionId: chatClientSessionId, startedAt: chatSessionStartedAt };
    }
    const clientSessionId = crypto.randomUUID();
    const startedAt = new Date().toISOString();
    setChatClientSessionId(clientSessionId);
    setChatSessionStartedAt(startedAt);
    return { clientSessionId, startedAt };
  };

  const clearChatSession = () => {
    setChatHistory([]);
    setChatInput("");
    setChatAuditTurns([]);
    setChatSessionStartedAt(null);
    setChatClientSessionId(null);
    sessionStorage.removeItem(CHAT_AUDIT_STORAGE_KEY);
  };

  const downloadChatAudit = async () => {
    if (chatAuditTurns.length === 0 || !chatClientSessionId || !chatSessionStartedAt) return;
    setChatAuditLoading(true);
    try {
      const created = await adminPostJson<{ downloadId: string; clientSessionId: string }>("/admin/api/chat/audit", {
        sessionMeta: { clientSessionId: chatClientSessionId, startedAt: chatSessionStartedAt },
        turns: chatAuditTurns,
      });
      const { blob, filename } = await adminDownloadFile(
        `/admin/api/chat/audit/download/${created.clientSessionId}/${created.downloadId}`,
      );
      triggerBlobDownload(blob, filename);
      toast.success("Pobrano audyt czatu");
    } catch (e) {
      toast.error("Nie udało się pobrać audytu", String((e as Error).message));
    } finally {
      setChatAuditLoading(false);
    }
  };

  const [sipTestLoading, setSipTestLoading] = useState(false);
  const [sipTestResult, setSipTestResult] = useState<SipRegisterTestResult | null>(null);
  const [sipCooldownSec, setSipCooldownSec] = useState(0);

  const [calls, setCalls] = useState<CallSession[]>([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [callsErr, setCallsErr] = useState<string | null>(null);
  const [callDetail, setCallDetail] = useState<CallSessionDetail | null>(null);
  const [auditLoadingId, setAuditLoadingId] = useState<string | null>(null);
  const [recordingLoadingId, setRecordingLoadingId] = useState<string | null>(null);
  const [selectedCallIds, setSelectedCallIds] = useState<Set<string>>(() => new Set());
  const [bulkAuditLoading, setBulkAuditLoading] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [noteModal, setNoteModal] = useState<CallNoteModalState | null>(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    setSettingsErr(null);
    try {
      const r = await adminFetch<{ settings: Settings }>("/admin/api/settings");
      setSettings(r.settings);
      const [live, chat, voices] = await Promise.all([
        adminFetch<{ models: ModelOpt[] }>("/admin/api/models?kind=live"),
        adminFetch<{ models: ModelOpt[] }>("/admin/api/models?kind=chat"),
        adminFetch<{ voices: VoiceOpt[] }>("/admin/api/voices/gemini"),
      ]);
      setLiveModels(live.models);
      setChatModels(chat.models);
      setGeminiVoices(voices.voices);
    } catch (e) {
      setSettingsErr(String((e as Error).message));
    }
  }, []);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (sipCooldownSec <= 0) return;
    const id = window.setInterval(() => {
      setSipCooldownSec((sec) => (sec <= 1 ? 0 : sec - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [sipCooldownSec]);

  const startSipCooldown = (seconds: number) => setSipCooldownSec(Math.max(1, seconds));

  const saveSettings = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const r = await adminFetch<{ settings: Settings }>("/admin/api/settings", {
        method: "PATCH",
        body: JSON.stringify(settings),
      });
      setSettings(r.settings);
      toast.success("Zapisano zmiany", "Obowiązują od nowych połączeń.");
    } catch (e) {
      toast.error("Nie udało się zapisać", String((e as Error).message));
    } finally {
      setSaving(false);
    }
  };

  const testLlm = async () => {
    try {
      const r = await adminFetch<{ reply: string }>("/admin/api/test-llm", { method: "POST" });
      toast.success("Test LLM zakończony", r.reply);
    } catch (e) {
      toast.error("Test LLM nieudany", String((e as Error).message));
    }
  };

  const testSipRegister = async () => {
    setSipTestLoading(true);
    try {
      const res = await fetch("/admin/api/test-sip-register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const data = (await res.json().catch(() => ({}))) as SipRegisterTestResult & { error?: string; retryAfterSec?: number };
      if (res.status === 429) {
        startSipCooldown(data.retryAfterSec ?? 30);
        toast.warning("Ponowny test SIP możliwy za chwilę.", data.error);
        return;
      }
      if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
      setSipTestResult(data);
      startSipCooldown(30);
    } catch (e) {
      toast.error("Test SIP nieudany", String((e as Error).message));
    } finally {
      setSipTestLoading(false);
    }
  };

  const sendChat = async () => {
    const msg = chatInput.trim();
    if (!msg) return;
    ensureChatSession();
    setChatLoading(true);
    const history = [...chatHistory];
    setChatHistory([...history, { role: "user", text: msg }]);
    setChatInput("");
    try {
      const r = await adminFetch<{ reply: string; turnAudit: ChatTurnAudit }>("/admin/api/chat", {
        method: "POST",
        body: JSON.stringify({ message: msg, history }),
      });
      setChatHistory((h) => [...h, { role: "model", text: r.reply }]);
      setChatAuditTurns((turns) => [...turns, r.turnAudit]);
    } catch (e) {
      toast.error("Błąd czatu", String((e as Error).message));
    } finally {
      setChatLoading(false);
    }
  };

  const loadCalls = useCallback(async () => {
    setCallsLoading(true);
    setCallsErr(null);
    try {
      const r = await adminFetch<{ sessions: CallSession[] }>("/admin/api/calls?limit=50");
      setCalls(r.sessions);
      setSelectedCallIds(new Set());
    } catch (e) {
      setCallsErr(String((e as Error).message));
    } finally {
      setCallsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "transcripts") void loadCalls();
  }, [tab, loadCalls]);

  const bulkDownloadAudits = async () => {
    setBulkAuditLoading(true);
    try {
      const created = await adminPostJson<{ downloadId: string }>("/admin/api/calls/audit/bulk", {
        sessionIds: [...selectedCallIds],
      });
      const { blob, filename } = await adminDownloadFile(`/admin/api/calls/audit/bulk/download/${created.downloadId}`);
      triggerBlobDownload(blob, filename);
      toast.success("Pobrano audyty");
    } catch (e) {
      toast.error("Nie udało się pobrać audytów", String((e as Error).message));
    } finally {
      setBulkAuditLoading(false);
    }
  };

  const bulkDelete = async () => {
    const count = selectedCallIds.size;
    const ok = await confirm({
      title: `Usunąć ${count} ${count === 1 ? "zaznaczoną rozmowę" : "zaznaczonych rozmów"}?`,
      body: "Rozmowy wraz z nagraniami zostaną usunięte. Tej operacji nie można cofnąć.",
      confirmLabel: "Usuń",
      danger: true,
    });
    if (!ok) return;
    setBulkDeleteLoading(true);
    try {
      await adminPostJson<{ deleted: number }>("/admin/api/calls/delete/bulk", { sessionIds: [...selectedCallIds] });
      const deletedIds = new Set(selectedCallIds);
      if (callDetail && deletedIds.has(callDetail.id)) setCallDetail(null);
      if (noteModal && deletedIds.has(noteModal.sessionId)) setNoteModal(null);
      await loadCalls();
      toast.success("Usunięto rozmowy");
    } catch (e) {
      toast.error("Nie udało się usunąć", String((e as Error).message));
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const saveNote = async () => {
    if (!noteModal) return;
    setNoteSaving(true);
    try {
      const updated = await adminFetch<{ id: string; adminNote: string | null; hasAdminNote: boolean }>(
        `/admin/api/calls/${noteModal.sessionId}/note`,
        { method: "PATCH", body: JSON.stringify({ note: noteDraft }) },
      );
      setCalls((prev) =>
        prev.map((row) =>
          row.id === updated.id ? { ...row, adminNote: updated.adminNote, hasAdminNote: updated.hasAdminNote } : row,
        ),
      );
      setNoteModal(null);
      toast.success("Zapisano notatkę");
    } catch (err) {
      toast.error("Nie udało się zapisać notatki", String((err as Error).message));
    } finally {
      setNoteSaving(false);
    }
  };

  const meta = PAGE_META[tab];

  let content: ReactNode;
  if (!settings && settingsErr && (tab === "settings" || tab === "settingsTech")) {
    content = (
      <div className="panel">
        <EmptyState
          title="Nie udało się pobrać ustawień"
          desc={settingsErr}
          icon={<IconAlertCircle size={22} />}
          action={
            <button type="button" className="btn-secondary" onClick={() => void loadSettings()}>
              Spróbuj ponownie
            </button>
          }
        />
      </div>
    );
  } else if (!settings && (tab === "settings" || tab === "settingsTech")) {
    content = (
      <div className="panel">
        <LoadingState text="Ładowanie ustawień..." />
      </div>
    );
  } else if (settings && (tab === "settings" || tab === "settingsTech")) {
    content = (
      <SettingsTab
        mode={tab === "settingsTech" ? "advanced" : "basic"}
        settings={settings}
        setSettings={setSettings}
        liveModels={liveModels}
        chatModels={chatModels}
        geminiVoices={geminiVoices}
        saving={saving}
        onSave={saveSettings}
        onTestLlm={() => void testLlm()}
        onTestSipRegister={() => void testSipRegister()}
        sipTestLoading={sipTestLoading}
        sipTestResult={sipTestResult}
        sipCooldownSec={sipCooldownSec}
      />
    );
  } else if (tab === "sipTrunks") {
    content = <SipTrunksPanel />;
  } else if (tab === "contacts") {
    content = <ContactsPanel />;
  } else if (tab === "crmContacts") {
    content = <CrmContactsPanel />;
  } else if (tab === "logs") {
    content = <AdminLogsPanel setErr={setErr} />;
  } else if (tab === "call") {
    content = <PanelCallPanel />;
  } else if (tab === "chat") {
    content = (
      <div className="panel">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <div>
            <h3 style={{ margin: 0 }}>Chat testowy</h3>
            {chatAuditTurns.length > 0 ? (
              <p className="muted" style={{ marginTop: 4 }}>
                Audyt: {chatAuditTurns.length} {chatAuditTurns.length === 1 ? "tura" : "tur"} w buforze
              </p>
            ) : null}
          </div>
          <div className="row" style={{ gap: 8 }}>
            <button type="button" className="btn-secondary" disabled={chatAuditTurns.length === 0 || chatAuditLoading} onClick={() => void downloadChatAudit()}>
              {chatAuditLoading ? "Pobieranie..." : "Pobierz audyt"}
            </button>
            <button type="button" className="btn-secondary" disabled={chatHistory.length === 0 && chatAuditTurns.length === 0} onClick={clearChatSession}>
              Wyczyść czat
            </button>
          </div>
        </div>
        <div className="chat-log" style={{ marginTop: 16 }}>
          {chatHistory.length === 0 ? (
            <div className="chat-empty">Zacznij rozmowę - napisz wiadomość poniżej.</div>
          ) : (
            chatHistory.map((m, i) => (
              <div key={i} className={`chat-msg chat-msg--${m.role}`}>
                <strong>{m.role === "user" ? "Ty" : "Asystentka"}</strong>
                {m.text}
              </div>
            ))
          )}
        </div>
        <div className="chat-composer">
          <textarea
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void sendChat();
              }
            }}
            placeholder="Wiadomość... (Enter wysyła, Shift+Enter nowa linia)"
            rows={1}
          />
          <button type="button" className="btn" disabled={chatLoading || !chatInput.trim()} onClick={() => void sendChat()}>
            {chatLoading ? "Wysyłam..." : "Wyślij"}
          </button>
        </div>
      </div>
    );
  } else if (tab === "transcripts") {
    content = (
      <div className="view-stack">
      <div className="panel">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <h3 style={{ margin: 0 }}>Transkrypty rozmów</h3>
          {selectedCallIds.size > 0 && (
            <div className="row" style={{ gap: 8 }}>
              <span className="muted">Zaznaczono: {selectedCallIds.size}</span>
              <button type="button" className="btn btn--small" disabled={bulkAuditLoading || bulkDeleteLoading} onClick={() => void bulkDownloadAudits()}>
                {bulkAuditLoading ? "Pobieranie..." : "Pobierz zaznaczone audyty"}
              </button>
              <button type="button" className="btn-danger btn--small" disabled={bulkAuditLoading || bulkDeleteLoading} onClick={() => void bulkDelete()}>
                {bulkDeleteLoading ? "Usuwanie..." : "Usuń zaznaczone"}
              </button>
            </div>
          )}
        </div>

        {callsLoading ? (
          <LoadingState text="Ładowanie transkryptów..." />
        ) : callsErr ? (
          <EmptyState
            title="Nie udało się pobrać transkryptów"
            desc={callsErr}
            icon={<IconAlertCircle size={22} />}
            action={
              <button type="button" className="btn-secondary" onClick={() => void loadCalls()}>
                Spróbuj ponownie
              </button>
            }
          />
        ) : calls.length === 0 ? (
          <EmptyState title="Brak rozmów" desc="Tu pojawią się transkrypty po pierwszej rozmowie z asystentką." icon={<IconFileText size={22} />} />
        ) : (
          <div className="admin-table-wrap" style={{ marginTop: 16 }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <Checkbox
                      ariaLabel="Zaznacz wszystkie"
                      checked={calls.length > 0 && calls.every((c) => selectedCallIds.has(c.id))}
                      indeterminate={selectedCallIds.size > 0 && !calls.every((c) => selectedCallIds.has(c.id))}
                      onChange={(checked) => setSelectedCallIds(checked ? new Set(calls.map((c) => c.id)) : new Set())}
                    />
                  </th>
                  <th>Data</th>
                  <th>Numer</th>
                  <th>
                    <span className="field-label-row">
                      Wypowiedzi
                      <HelpTooltip text={CALL_ENTRIES_HELP} label="Wypowiedzi - wyjaśnienie" />
                    </span>
                  </th>
                  <th aria-label="Akcje" />
                </tr>
              </thead>
              <tbody>
                {calls.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <Checkbox
                        ariaLabel={`Zaznacz rozmowę ${c.callerNumber ?? c.id}`}
                        checked={selectedCallIds.has(c.id)}
                        onChange={(checked) =>
                          setSelectedCallIds((prev) => {
                            const next = new Set(prev);
                            if (checked) next.add(c.id);
                            else next.delete(c.id);
                            return next;
                          })
                        }
                      />
                    </td>
                    <td>{new Date(c.startedAt).toLocaleString("pl-PL")}</td>
                    <td>
                      <div>{c.callerNumber ?? "-"}</div>
                      {c.callerName && <div className="muted">{c.callerName}</div>}
                    </td>
                    <td>{c.entryCount}</td>
                    <td>
                      <div className="row" style={{ gap: 8, justifyContent: "flex-end", flexWrap: "nowrap" }}>
                        <button
                          type="button"
                          className="btn-secondary btn--small"
                          onClick={async () => {
                            try {
                              const d = await adminFetch<CallSessionDetail>(`/admin/api/calls/${c.id}`);
                              setCallDetail(d);
                            } catch (e) {
                              toast.error("Nie udało się pobrać rozmowy", String((e as Error).message));
                            }
                          }}
                        >
                          Podgląd
                        </button>
                        <button
                          type="button"
                          className={`btn-secondary btn--small${c.hasAdminNote ? " has-note" : ""}`}
                          onClick={() => {
                            setNoteDraft(c.adminNote ?? "");
                            setNoteModal({ sessionId: c.id, callerNumber: c.callerNumber, startedAt: c.startedAt, adminNote: c.adminNote });
                          }}
                        >
                          {c.hasAdminNote ? "Notatka •" : "Notatka"}
                        </button>
                        {c.hasRecording && (
                          <button
                            type="button"
                            className="btn-secondary btn--small"
                            disabled={recordingLoadingId === c.id}
                            onClick={async () => {
                              setRecordingLoadingId(c.id);
                              try {
                                const { blob, filename } = await adminDownloadFile(`/admin/api/calls/${c.id}/recording`);
                                triggerBlobDownload(blob, filename);
                              } catch (e) {
                                toast.error("Nie udało się pobrać nagrania", String((e as Error).message));
                              } finally {
                                setRecordingLoadingId(null);
                              }
                            }}
                          >
                            {recordingLoadingId === c.id ? "Pobieranie..." : "Nagranie"}
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn--small"
                          disabled={auditLoadingId === c.id}
                          onClick={async () => {
                            setAuditLoadingId(c.id);
                            try {
                              const created = await adminPostJson<{ downloadId: string }>(`/admin/api/calls/${c.id}/audit`, {});
                              const { blob, filename } = await adminDownloadFile(`/admin/api/calls/${c.id}/audit/download/${created.downloadId}`);
                              triggerBlobDownload(blob, filename);
                            } catch (e) {
                              toast.error("Nie udało się pobrać audytu", String((e as Error).message));
                            } finally {
                              setAuditLoadingId(null);
                            }
                          }}
                        >
                          {auditLoadingId === c.id ? "Pobieranie..." : "Audyt"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>

      {callDetail && (
          <div className="panel">
            <div className="row" style={{ justifyContent: "space-between" }}>
              <h3 style={{ margin: 0 }}>Podgląd rozmowy</h3>
              <button type="button" className="btn-secondary btn--small" onClick={() => setCallDetail(null)}>
                Zamknij
              </button>
            </div>
            <dl style={{ marginTop: 12, display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 16px" }}>
              <dt className="muted">Call ID</dt>
              <dd style={{ margin: 0 }}>{callDetail.channelId ?? "-"}</dd>
              <dt className="muted">Nazwa</dt>
              <dd style={{ margin: 0 }}>{callDetail.callerName ?? "-"}</dd>
              <dt className="muted">Numer</dt>
              <dd style={{ margin: 0 }}>{callDetail.callerNumber ?? "-"}</dd>
              <dt className="muted">Start</dt>
              <dd style={{ margin: 0 }}>{new Date(callDetail.startedAt).toLocaleString("pl-PL")}</dd>
              <dt className="muted">Koniec</dt>
              <dd style={{ margin: 0 }}>{callDetail.endedAt ? new Date(callDetail.endedAt).toLocaleString("pl-PL") : "-"}</dd>
              <dt className="muted">Transfer</dt>
              <dd style={{ margin: 0 }}>{callDetail.transferPending ? "oczekujący" : "nie"}</dd>
            </dl>
            <div className="transcript-list" style={{ marginTop: 16 }}>
              {callDetail.entries.map((e, i) => (
                <TranscriptEntryBlock key={`${e.secondsFromStart}-${e.speaker}-${i}`} entry={e} />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <AppShell nav={NAV} activeId={tab} onSelect={(id) => setTab(id as TabId)} onToggleTheme={onToggleTheme} onLogout={onLogout} appName="CallBot">
      <PageHead title={meta.title} sub={meta.sub} />
      {err && <ErrorBanner message={err} />}
      {content}

      <Modal
        open={noteModal != null}
        onClose={() => (noteSaving ? undefined : setNoteModal(null))}
        labelledBy="call-note-modal-title"
        panelClassName="modal__panel call-note-modal__panel"
      >
        {noteModal && (
          <>
            <div className="interventions-modal__header">
              <div>
                <h2 id="call-note-modal-title" className="modal__title">
                  Notatka
                </h2>
                <p className="muted" style={{ margin: "4px 0 0" }}>
                  {new Date(noteModal.startedAt).toLocaleString("pl-PL")}
                  {noteModal.callerNumber ? ` · ${noteModal.callerNumber}` : ""}
                </p>
              </div>
              <button type="button" className="btn-icon-only" onClick={() => setNoteModal(null)} aria-label="Zamknij">
                <IconX size={16} />
              </button>
            </div>
            <form
              className="call-note-modal__body"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                void saveNote();
              }}
            >
              <textarea
                className="call-note-modal__textarea"
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                rows={6}
                maxLength={5000}
                placeholder="Notatka do rozmowy..."
                autoFocus
              />
              <div className="modal__actions" style={{ marginTop: 12 }}>
                <button type="button" className="btn-secondary" onClick={() => setNoteModal(null)} disabled={noteSaving}>
                  Anuluj
                </button>
                <button type="submit" className="btn" disabled={noteSaving}>
                  {noteSaving ? "Zapisywanie..." : "Zapisz"}
                </button>
              </div>
            </form>
          </>
        )}
      </Modal>
    </AppShell>
  );
}

export function CallbotAdminApp() {
  const { theme, toggleTheme } = useAdminTheme();
  const [authed, setAuthed] = useState<boolean | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    adminFetch<{ authenticated: boolean }>("/admin/api/session")
      .then((r) => setAuthed(r.authenticated))
      .catch(() => setAuthed(false));
  }, []);

  const handleLogout = async () => {
    try {
      await adminFetch("/admin/api/logout", { method: "POST" });
    } catch {
      /* still clear client session */
    }
    setAuthed(false);
    navigate("/admin", { replace: true });
  };

  if (authed === null) {
    return (
      <div className="admin-shell">
        <LoadingState text="Ładowanie..." />
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          authed ? (
            <Navigate to="/admin/panel" replace />
          ) : (
            <AdminLogin
              theme={theme}
              onToggleTheme={toggleTheme}
              onOk={() => {
                setAuthed(true);
                navigate("/admin/panel", { replace: true });
              }}
            />
          )
        }
      />
      <Route
        path="/panel"
        element={
          authed ? (
            <AdminPanel onToggleTheme={toggleTheme} onLogout={() => void handleLogout()} />
          ) : (
            <Navigate to="/admin" replace />
          )
        }
      />
      <Route path="*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
}
